import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import {
    getFirestore, collection, doc, setDoc, getDoc, getDocs, onSnapshot,
    serverTimestamp, query, where, writeBatch, documentId, updateDoc, increment, deleteDoc
} from 'firebase/firestore';
import {
    Trophy as TrophyIcon,
    Check as CheckIcon,
    CheckCheck,
    RefreshCw,
    RefreshCw as RefreshIcon,
    Trash2,
    ArrowUp,
    Zap as ZapIcon,
    DollarSign,
    Shield,
    ShoppingBag,
    Users,
    ArrowLeft,
    CreditCard,
    Calendar,
    Tv,
    LogOut,
    Info,
    X,
    TrendingUp,
    Activity,
    Lock,
    Unlock,
    Star,
    Copy,
    Edit3,
    Bell,
    BookOpen,
    Sparkles,
    CalendarX,
    MessageSquare,
    Link as LinkIcon
} from 'lucide-react';
import { DateTime } from 'luxon';

// --- 1. CONFIGURACIÓN ---
const firebaseConfig = {
    apiKey: "AIzaSyCqvDotQKqoKExPktJRnlFpI_TWqWBPyqI",
    authDomain: "futdate-7e533.firebaseapp.com",
    projectId: "futdate-7e533",
    storageBucket: "futdate-7e533.firebasestorage.app",
    messagingSenderId: "721246803667",
    appId: "1:721246803667:web:69612485d3644c76e9a626",
    measurementId: "G-7H7CL4MPP4"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const provider = new GoogleAuthProvider();

// El navegador YA NO habla con api-sports.io ni conoce ninguna clave.
// Todo pasa por el proxy (Cloud Function), que es quien tiene la clave
// guardada en Secret Manager. Es el mismo proxy que usa FUTDATE.
const PROXY_URL = "https://apifootballproxy-i6fyrjwbmq-uc.a.run.app";

// Convierte "/fixtures?date=2026-08-13" en la URL del proxy
const buildProxyUrl = (endpoint) =>
    `${PROXY_URL}/?endpoint=${encodeURIComponent(endpoint)}`;

// --- 2. CONSTANTES ---
const COMPETITIONS = {
    WORLD_CUP_26: { id: '1', name: 'Mundial 2026', season: '2026', type: 'free', status: 'inactive', isWorldCup: true },
    LIGA_MX: { id: '262', name: 'Liga MX', season: '2026', type: 'free', status: 'active', roundFilter: 'Apertura' },
    LEAGUES_CUP: { id: '772', name: 'Leagues Cup', season: '2026', type: 'free', status: 'inactive' },
    PREMIER_LEAGUE: { id: '39', name: 'Premier League', season: '2026', type: 'premium', status: 'active' },
    LA_LIGA: { id: '140', name: 'La Liga', season: '2026', type: 'premium', status: 'active' },
    SERIE_A: { id: '135', name: 'Serie A', season: '2026', type: 'free', status: 'active' },
    CHAMPIONS_LEAGUE: { id: '2', name: 'Champions League', season: '2026', type: 'premium', status: 'active' }
};

const STORE_ITEMS = {
    PACK_100_COINS: { id: 'PACK_100_COINS', name: '100 Futsie Coins', price: 10, type: 'coins', amount: 100 },
    PACK_500_COINS: { id: 'PACK_500_COINS', name: '550 Futsie Coins', price: 50, type: 'coins', amount: 550 },
    PACK_1000_COINS: { id: 'PACK_1000_COINS', name: '1200 Futsie Coins', price: 100, type: 'coins', amount: 1200 },
    PASS_PREMIER: { id: 'PASS_PREMIER', name: 'Pase Premier League', price: 150, type: 'access', description: 'Acceso total a la temporada.' },
    PASS_LALIGA: { id: 'PASS_LALIGA', name: 'Pase La Liga', price: 150, type: 'access', description: 'Acceso total a la temporada.' },
    PASS_SERIEA: { id: 'PASS_SERIEA', name: 'Pase Serie A', price: 150, type: 'access', description: 'Acceso total a la temporada.' },
    PASS_CHAMPIONS: { id: 'PASS_CHAMPIONS', name: 'Pase Champions', price: 200, type: 'access', description: 'Acceso total a la temporada.' },
    POWERUP_CORAZONADA: { id: 'POWERUP_CORAZONADA', name: 'Corazonada (x2 Puntos)', price: 50, type: 'powerup', description: 'Duplica los puntos obtenidos en un partido.' },
};

const broadcasterMap = {
    "america": ["Canal 5", "TUDN", "ViX"], "atlas": ["Azteca 7", "TUDN"], "cruz azul": ["Canal 5", "TUDN"],
    "guadalajara": ["Amazon Prime"], "monterrey": ["Canal 5", "TUDN"], "pumas": ["Las Estrellas", "TUDN"],
    "tigres uanl": ["Azteca 7"], "toluca": ["Canal 5"], "leon": ["Fox Sports"], "pachuca": ["Fox Sports"],
    "santos": ["TUDN"], "necaxa": ["TUDN"], "mazatlan": ["Azteca 7"], "tijuana": ["Caliente TV"],
    "queretaro": ["Caliente TV"], "juarez": ["Fox Sports"], "atletico de san luis": ["ESPN"], "puebla": ["Azteca 7"]
};

// --- 3. UTILIDADES ---
const normalizeTeamName = (name) => name ? name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/fc|club|u\.n\.a\.m\.|chivas|\s*-\s*/g, '').trim() : '';

const apiClient = async (endpoint, retries = 3) => {
    try {
        const response = await fetch(buildProxyUrl(endpoint));
        if (response.status === 429 && retries > 0) {
            await new Promise(res => setTimeout(res, 1500));
            return apiClient(endpoint, retries - 1);
        }
        const data = await response.json();
        return data.response || null;
    } catch (err) {
        if (retries > 0) {
            await new Promise(res => setTimeout(res, 2000));
            return apiClient(endpoint, retries - 1);
        }
        return null;
    }
};

const formatRoundName = (roundName) => {
    if (!roundName) return "";
    const number = roundName.replace(/[^0-9]/g, '');
    if (roundName.toLowerCase().includes('group')) return `Jornada ${number}`;
    if (roundName.toLowerCase().includes('16')) return `16avos de Final`;
    if (roundName.toLowerCase().includes('8') || roundName.toLowerCase().includes('eighth')) return `8avos de Final`;
    if (roundName.toLowerCase().includes('quarter')) return `4tos de Final`;
    if (roundName.toLowerCase().includes('semi')) return `Semifinal`;
    if (roundName.toLowerCase().includes('final')) return `Gran Final`;
    return number ? `Jornada ${number}` : roundName.replace("Regular Season - ", "").replace("Clausura - ", "").replace("Apertura - ", "");
};

const shortRoundName = (roundName) => {
    if (!roundName) return "";
    const lower = roundName.toLowerCase();
    const number = roundName.replace(/[^0-9]/g, '');
    if (lower.includes('group')) return `J${number}`;
    if (lower.includes('16')) return `16avos`;
    if (lower.includes('8') || lower.includes('eighth')) return `8avos`;
    if (lower.includes('quarter')) return `4tos`;
    if (lower.includes('semi')) return `Semi`;
    if (lower.includes('final')) return `Final`;
    return number ? `J${number}` : roundName.substring(0, 3);
};

const calculateMatchPoints = (fixture, prediction, isWorldCupKnockout = false) => {
    if (!prediction || fixture.goals.home === null || fixture.goals.away === null) return { total: 0, exact: false, outcome: false };
    const { home: goalsHome, away: goalsAway } = fixture.goals;
    const { predHomeGoals, predAwayGoals, predictedOutcome, predictedPenaltyWinner, predictedWinMethod, usedPowerUp } = prediction;
    const isLeaguesCup = fixture.league.id.toString() === COMPETITIONS.LEAGUES_CUP.id;
    let totalPoints = 0;
    let outcomeCorrect = false;
    let exactCorrect = false;

    let realOutcome = goalsHome > goalsAway ? 'home' : (goalsAway > goalsHome ? 'away' : 'draw');
    const exactScoreBonus = (parseInt(predHomeGoals) === goalsHome && parseInt(predAwayGoals) === goalsAway) ? 2 : 0;
    if (exactScoreBonus > 0) exactCorrect = true;

    if (isWorldCupKnockout) {
        if (predictedOutcome === realOutcome) {
            totalPoints = 3;
            outcomeCorrect = true;
        }
        let realMethod = '90MIN';
        if (fixture.score?.penalty?.home !== null) realMethod = 'PEN';
        else if (fixture.score?.extratime?.home !== null) realMethod = 'ET';
        if (predictedWinMethod === realMethod) {
            totalPoints += 2;
        }
    } 
    else if (isLeaguesCup) {
        if (realOutcome === 'draw') {
            if (predictedOutcome === 'draw') {
                totalPoints = 2;
                outcomeCorrect = true;
                const wasPenalties = fixture.score?.penalty?.home !== null;
                if (wasPenalties) {
                    const realPenWinner = fixture.score.penalty.home > fixture.score.penalty.away ? 'home' : 'away';
                    if (predictedPenaltyWinner === realPenWinner) totalPoints += 2;
                }
            }
        } else if (predictedOutcome === realOutcome) {
            totalPoints = 3;
            outcomeCorrect = true;
        }
        totalPoints += exactScoreBonus;
    } 
    else {
        if (predictedOutcome === realOutcome) {
            totalPoints = 3;
            outcomeCorrect = true;
        }
        if (totalPoints > 0) totalPoints += exactScoreBonus;
    }
    
    if (usedPowerUp === 'POWERUP_CORAZONADA' && totalPoints > 0) totalPoints *= 2;
    return { total: totalPoints, exact: exactCorrect, outcome: outcomeCorrect };
};

const getAvatarColor = (name) => {
    const colors = [
        'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-yellow-500', 'bg-lime-500',
        'bg-green-500', 'bg-emerald-500', 'bg-teal-500', 'bg-cyan-500', 'bg-sky-500',
        'bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500',
        'bg-pink-500', 'bg-rose-500'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

// --- 4. COMPONENTES VISUALES ---

const WorldCupBackground = () => (
    <div className="fixed inset-0 w-full h-full -z-10 overflow-hidden bg-[#170A29]">
        <svg className="absolute min-w-full min-h-full w-auto h-auto object-cover opacity-80" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
            <rect width="800" height="600" fill="#4E11F4"/>
            <path d="M0,0 h400 v300 h-400 z" fill="#85E04F"/>
            <path d="M400,0 a200,200 0 0,0 -200,200 h200 z" fill="#4E11F4"/>
            <path d="M0,300 a200,200 0 0,0 200,-200 v-100 h-200 z" fill="#0F2573"/>
            <path d="M400,0 h400 v300 h-400 z" fill="#0F2573"/>
            <path d="M800,0 a200,200 0 0,1 -200,200 h200 z" fill="#E94822"/>
            <path d="M400,300 a200,200 0 0,0 200,-200 v-100 h-200 z" fill="#2D9350"/>
            <path d="M0,300 h400 v300 h-400 z" fill="#E94822"/>
            <circle cx="200" cy="450" r="150" fill="#4E11F4"/>
            <path d="M0,600 a200,200 0 0,1 200,-200 v200 z" fill="#85E04F"/>
            <path d="M400,300 h400 v300 h-400 z" fill="#2D9350"/>
            <circle cx="600" cy="450" r="150" fill="#E94822"/>
            <path d="M800,600 a200,200 0 0,0 -200,-200 v200 z" fill="#7A1B16"/>
        </svg>
        <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[1px]"></div>
    </div>
);

const GlobalStylesLoader = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@800;900&display=swap');

        body {
            font-family: 'Inter', sans-serif;
            background-color: #f2f2f7;
            -webkit-font-smoothing: antialiased;
            overscroll-behavior: none;
        }
        .font-oswald { font-family: 'Oswald', sans-serif; }
        .font-wc { font-family: 'Outfit', sans-serif; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .glass-dark { background: rgba(15, 23, 42, 0.9); backdrop-filter: blur(16px); border-bottom: 1px solid rgba(255,255,255,0.05); }

        .perspective-2000 { perspective: 2000px; }
        .transform-style-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }

        @keyframes flipInY {
            0% { transform: perspective(2000px) rotateY(-180deg) scale(0.5); opacity: 0; }
            60% { transform: perspective(2000px) rotateY(10deg) scale(1.05); opacity: 1; }
            100% { transform: perspective(2000px) rotateY(0deg) scale(1); opacity: 1; }
        }

        @keyframes flipOutY {
            0% { transform: perspective(2000px) rotateY(0deg) scale(1); opacity: 1; }
            40% { transform: perspective(2000px) rotateY(10deg) scale(1.05); opacity: 1; }
            100% { transform: perspective(2000px) rotateY(-180deg) scale(0.5); opacity: 0; }
        }

        @keyframes slideDownOut {
            0% { transform: translateY(0) scale(1); opacity: 1; }
            100% { transform: translateY(100vh) scale(0.8); opacity: 0; }
        }

        .animate-flip-in { animation: flipInY 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
        .animate-flip-out { animation: flipOutY 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; }
        .animate-slide-down-out { animation: slideDownOut 0.5s cubic-bezier(0.55, 0.085, 0.68, 0.53) forwards; }

        @keyframes notifSlideDown {
             from { transform: translateY(-100%); opacity: 0; }
             to { transform: translateY(0); opacity: 1; }
        }
        @keyframes notifSlideUpOut {
             from { transform: translateY(0); opacity: 1; }
             to { transform: translateY(-100%); opacity: 0; }
        }
        .animate-notif-slide { animation: notifSlideDown 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-notif-out { animation: notifSlideUpOut 0.3s ease-in forwards; }

        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        .animate-fade-in { animation: fadeIn 0.3s ease-out; }

        .bg-arena {
            background-color: #2e8b57;
            background-image: 
                radial-gradient(circle at 50% 50%, transparent 0, transparent 50px, rgba(255,255,255,0.4) 51px, rgba(255,255,255,0.4) 53px, transparent 54px),
                radial-gradient(circle at 50% 50%, white 3px, transparent 4px),
                linear-gradient(to bottom, rgba(0,0,0,0.05) 50%, transparent 50%);
            background-size: 100% 100%, 100% 100%, 100% 100px;
            position: relative;
        }
        
        .bg-arena::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 0;
            right: 0;
            height: 2px;
            background-color: rgba(255,255,255,0.4);
            transform: translateY(-50%);
            pointer-events: none;
        }
    `}</style>
);

const FutsieLogo = ({ light, scale = 1, className = "" }) => {
    const color = light ? "#FFFFFF" : "#0f172a";
    const customBlue = "#0071bc"; 
    const width = 160 * scale;
    
    return (
        <div className={`flex items-center justify-center cursor-default select-none transition-transform duration-300 ${className}`} style={{ transformOrigin: 'center center' }}>
            <svg width={width} viewBox="0 0 933.86 482.13" className="pointer-events-none drop-shadow-md">
                <path fill="none" stroke={color} strokeWidth="20" strokeLinecap="round" strokeLinejoin="round" d="M90.41,13.29c2.39,123.87,3.34,313.72,8.84,437.47,23.72-54.02,48.98-179.93,72.7-233.95,2.44,68.61,16.55,136.7,41.39,199.8l94.35-217.71c-8.95,76.39,9.88,153.34,33.26,226.01,45.25-132.63,70.21-273.46,73.54-414.91,3.06,133.44-15.57,284.82,21.65,412,12.12-22.98,145.84-214.72,195.44-284.75-5.82,8.18-114.62,104.68-168.73,142.28,67.02-2.35,193.51.45,193.51.45-5.5-6.01-152.86,207.72-152.86,191.24,0,0,166.36-223.02,194.2-271.12,0,0-22.74,158.34-22.72,231.49,5.65-10.13,106.34-95.97,209.92-221.92l-148.33,58.9c16.71,78.6,18.5,109.33,37.71,156.16,0,0,59.35-36.81,149.59-183.56"/>
                <path fill="none" stroke={color} strokeWidth="20" strokeLinecap="round" strokeLinejoin="round" d="M26.31,14.35c122.89-4.26,240.13,2.17,363.32-.57C260.15,45.27,51.8,81,10,90.36"/>
                <line fill="none" stroke={color} strokeWidth="20" strokeLinecap="round" strokeMiterlimit="10" x1="10" y1="90.36" x2="842.88" y2="90.36"/>
                <path fill="none" stroke={color} strokeWidth="20" strokeLinecap="round" strokeLinejoin="round" d="M691.12,154.34c8.24-2.76,16.48-5.52,24.72-8.27,3.25,4.48,3.86,10.73,1.54,15.76-6.68-8.76-14.63-16.55-23.54-23.04"/>

                <path fill="none" stroke={customBlue} strokeWidth="20" strokeLinecap="round" strokeLinejoin="round" d="M631.58,137.25c-5.82,8.18-114.62,104.68-168.73,142.28,67.02-2.35,234.85,1.92,234.85,1.92-5.5-6.01-194.2,206.25-194.2,189.76"/>
                <path fill="none" stroke={customBlue} strokeWidth="20" strokeLinecap="round" strokeLinejoin="round" d="M90.41,13.29c2.39,123.87,3.34,313.72,8.84,437.47"/>
                <path fill="none" stroke={customBlue} strokeWidth="20" strokeLinecap="round" strokeLinejoin="round" d="M26.31,14.35c122.89-4.26,240.13,2.06,363.32-.57"/>
                <line fill="none" stroke={customBlue} strokeWidth="20" strokeLinecap="round" strokeMiterlimit="10" x1="10" y1="90.36" x2="316.1" y2="90.36"/>
            </svg>
        </div>
    );
};

const FutsieIconOnly = ({ size = 20, className, light = true }) => (
    <div className={`flex items-center justify-center relative z-10 ${className}`} style={{width: size * 4, height: size}}>
         <FutsieLogo light={light} scale={size / 100} />
    </div>
);

const FutsieCoinIcon = ({ className = "w-8 h-8" }) => (
    <div className={`${className} rounded-full shadow-2xl relative flex items-center justify-center select-none transform hover:scale-110 transition-transform duration-300`}>
        <svg viewBox="0 0 120 120" className="w-full h-full drop-shadow-xl filter drop-shadow-[0_4px_6px_rgba(0,0,0,0.5)]">
            <defs>
                <linearGradient id="coinGoldRich" x1="10%" y1="10%" x2="90%" y2="90%">
                    <stop offset="0%" stopColor="#FFF2AC" /> 
                    <stop offset="20%" stopColor="#FDB931" />
                    <stop offset="45%" stopColor="#D4AF37" />
                    <stop offset="70%" stopColor="#B48811" />
                    <stop offset="100%" stopColor="#8A6E2F" />
                </linearGradient>
                <linearGradient id="coinBevel" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FFFACD" />
                    <stop offset="30%" stopColor="#DAA520" />
                    <stop offset="60%" stopColor="#B8860B" />
                    <stop offset="100%" stopColor="#5C450C" />
                </linearGradient>
                <filter id="innerGlow">
                    <feFlood floodColor="#000" floodOpacity="0.4"/>
                    <feComposite operator="out" in2="SourceGraphic"/>
                    <feGaussianBlur stdDeviation="2"/>
                    <feComposite operator="atop" in2="SourceGraphic"/>
                </filter>
                <filter id="logoEmboss">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="1" result="blur"/>
                    <feOffset in="blur" dx="1" dy="2" result="offsetBlur"/>
                    <feSpecularLighting in="blur" surfaceScale="3" specularConstant="1" specularExponent="20" lightingColor="white" result="specOut">
                        <fePointLight x="-50" y="-50" z="200"/>
                    </feSpecularLighting>
                    <feComposite in="specOut" in2="SourceAlpha" operator="in" result="specOut"/>
                    <feComposite in="SourceGraphic" in2="offsetBlur" operator="over" result="final"/>
                </filter>
            </defs>
            <circle cx="60" cy="60" r="58" fill="url(#coinBevel)" stroke="#785C15" strokeWidth="0.5" />
            <circle cx="60" cy="60" r="50" fill="url(#coinGoldRich)" stroke="#B8860B" strokeWidth="0.5" filter="url(#innerGlow)" />
            <circle cx="60" cy="60" r="46" fill="none" stroke="#996515" strokeWidth="0.5" strokeDasharray="1 2" opacity="0.3" />
            <g transform="translate(36, 36) scale(1.6)" filter="url(#logoEmboss)">
               <path d="M10 2L24 2L18 13H26L8 30L12 16H4L10 2Z" fill="#754E0B" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5"/>
            </g>
            <path d="M 25 25 Q 60 5 95 25" stroke="rgba(255,255,255,0.6)" strokeWidth="4" strokeLinecap="round" fill="none" opacity="0.6" filter="url(#blur)" />
        </svg>
    </div>
);

const WhatsAppIcon = ({ size = 24, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
    </svg>
);

const CompetitionLogo = ({ competitionKey, ...props }) => {
    const map = {
        WORLD_CUP_26: "/mundial26.svg",
        LIGA_MX: "/logoligamx.svg",
        LEAGUES_CUP: "/leagues-cup-logo-2025.png",
        PREMIER_LEAGUE: "https://media.api-sports.io/football/leagues/39.png",
        LA_LIGA: "https://media.api-sports.io/football/leagues/140.png",
        SERIE_A: "https://media.api-sports.io/football/leagues/135.png",
        CHAMPIONS_LEAGUE: "https://media.api-sports.io/football/leagues/2.png"
    };
    return <img src={map[competitionKey] || map.LIGA_MX} alt={competitionKey} onError={(e) => {e.target.src='https://placehold.co/100?text=LIGA';}} {...props} />;
};

const WhistleIcon = ({ size = 24, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M5.5 12h2.5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2.5a2.5 2.5 0 0 1-2.5-2.5v-4a2.5 2.5 0 0 1 2.5-2.5z" />
        <path d="M8 12v-2a4 4 0 0 1 4-4h.5a4.5 4.5 0 0 1 4.5 4.5v.5" />
        <path d="M17 10.5a.5.5 0 0 0-.5.5v1.5a2.5 2.5 0 0 1-2.5 2.5h-2" />
    </svg>
);

const NotificationSystem = ({ notifications, onDismiss }) => {
    const [dismissingIds, setDismissingIds] = useState([]);

    useEffect(() => {
        if ('Notification' in window && Notification.permission !== 'granted' && Notification.permission !== 'denied') {
            Notification.requestPermission();
        }
    }, []);

    const handleTouchStart = (e, id) => { e.currentTarget.dataset.startY = e.touches[0].clientY; };
    const handleTouchEnd = (e, id) => {
        const startY = parseFloat(e.currentTarget.dataset.startY);
        const endY = e.changedTouches[0].clientY;
        if (startY - endY > 50) {
            setDismissingIds(prev => [...prev, id]);
            setTimeout(() => onDismiss(id), 300); 
        }
    };

    if (!notifications || notifications.length === 0) return null;

    return (
        <div className="fixed top-4 left-4 right-4 z-[150] space-y-2 pointer-events-none">
            {notifications.map((notif) => (
                <div
                    key={notif.id}
                    onTouchStart={(e) => handleTouchStart(e, notif.id)}
                    onTouchEnd={(e) => handleTouchEnd(e, notif.id)}
                    className={`pointer-events-auto p-4 rounded-xl shadow-2xl flex items-start gap-3 backdrop-blur-md border-l-4 transition-all duration-300 ${
                        dismissingIds.includes(notif.id) ? 'animate-notif-out' : 'animate-notif-slide'
                    } ${
                        notif.type === 'payment' ? 'bg-yellow-50/95 border-yellow-500 text-yellow-900' :
                        notif.type === 'results' ? 'bg-green-50/95 border-green-500 text-green-900' :
                        'bg-blue-50/95 border-blue-600 text-slate-800'
                    }`}
                >
                    <div className={`p-2 rounded-full ${
                        notif.type === 'payment' ? 'bg-yellow-200 text-yellow-700' :
                        notif.type === 'results' ? 'bg-green-200 text-green-700' :
                        'bg-blue-100 text-blue-600'
                    }`}>
                        {notif.type === 'payment' ? <DollarSign size={20}/> :
                         notif.type === 'results' ? <TrophyIcon size={20}/> :
                         <Bell size={20} />}
                    </div>
                    <div className="flex-1">
                        <h4 className="font-bold text-sm uppercase tracking-wide opacity-80">{notif.title}</h4>
                        <p className="text-xs font-medium mt-1 leading-snug">{notif.text}</p>
                    </div>
                    <button onClick={() => onDismiss(notif.id)} className="opacity-50 hover:opacity-100"><X size={16} /></button>
                </div>
            ))}
        </div>
    );
};

const AppFooter = ({ light = true }) => (
    <div className="relative z-10 w-full py-8 text-center bg-transparent flex flex-col items-center gap-2 mt-8 mb-24 border-t border-white/10 pt-6">
        <FutsieIconOnly size={60} light={light} className="opacity-80" />
        <p className="text-gray-400 text-[10px] font-bold tracking-widest uppercase">FUTSIE26.1</p>
    </div>
);

const UserAvatar = ({ url, name, size = "w-8 h-8", fontSize = "text-xs" }) => {
    const colorClass = useMemo(() => getAvatarColor(name || '?'), [name]);
    const [imgError, setImgError] = useState(false);

    useEffect(() => { setImgError(false); }, [url]);

    return (
        <div className={`${size} rounded-full flex-shrink-0 overflow-hidden border border-gray-200 shadow-sm ${(!url || imgError) ? colorClass : 'bg-white'} flex items-center justify-center`}>
            {url && !imgError ? (
                <img src={url} alt={name} className="w-full h-full object-cover" onError={() => setImgError(true)} />
            ) : (
                <span className={`${fontSize} font-bold text-white`}>{name?.charAt(0).toUpperCase()}</span>
            )}
        </div>
    );
};

const UpdateNotice = ({ onClose }) => (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
        <div className="bg-white p-8 rounded-3xl w-full max-w-sm text-center shadow-2xl">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6"><Shield size={40} className="text-blue-600"/></div>
            <h2 className="text-2xl font-black mb-3 text-gray-900">Transparencia Total 🛡️</h2>
            <p className="text-gray-500 mb-8 text-sm leading-relaxed">
                A partir de ahora, los pronósticos se mantienen <strong>ocultos</strong> y se revelan para todos en la pestaña Resultados exactamente al pitazo inicial del partido. ¡Cero trampas!
            </p>
            <button onClick={onClose} className="w-full bg-blue-600 text-white font-bold py-3.5 rounded-xl hover:bg-blue-700 transition-colors shadow-xl">¡Entendido!</button>
        </div>
    </div>
);

const LoginScreen = ({ onLogin }) => (
    <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="relative z-10 flex flex-col items-center animate-fade-in">
            <FutsieLogo scale={1.0} light />
            <p className="text-slate-400 mb-10 mt-12 max-w-xs text-sm font-medium leading-relaxed">La plataforma definitiva para quinielas de fútbol. Compite, gana y diviértete.</p>
            <button onClick={onLogin} className="flex items-center gap-4 bg-white text-slate-900 font-bold py-4 px-8 rounded-full shadow-2xl hover:scale-105 transition-all hover:shadow-white/20">
                <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-6 h-6" alt="G" />
                Continuar con Google
            </button>
        </div>
    </div>
);

const AliasModal = ({ isOpen, onClose, onSave, currentAlias }) => {
    const [alias, setAlias] = useState(currentAlias || '');
    useEffect(() => { if(isOpen) setAlias(currentAlias || ''); }, [isOpen, currentAlias]);
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-flip-in">
            <div className="bg-white p-6 rounded-3xl w-full max-w-sm shadow-2xl transform transition-all scale-100 border border-gray-100">
                <h3 className="text-xl font-black mb-1 text-gray-900">Editar Alias</h3>
                <p className="text-sm text-gray-500 mb-4">Este nombre se mostrará en todas tus ligas.</p>
                <input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="Tu nuevo alias..." className="w-full bg-gray-50 border border-gray-200 p-3 rounded-xl text-lg font-bold text-gray-900 outline-none focus:border-blue-500 transition-colors mb-6" maxLength={15} />
                <div className="flex gap-2">
                    <button onClick={onClose} className="flex-1 bg-gray-100 text-gray-600 font-bold py-3 rounded-xl hover:bg-gray-200 transition-colors text-sm">Cancelar</button>
                    <button onClick={() => onSave(alias)} className="flex-1 bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition-colors text-sm shadow-lg">Guardar</button>
                </div>
            </div>
        </div>
    );
};

const FutsieManual = ({ onClose }) => {
    const [isClosing, setIsClosing] = useState(false);
    const handleClose = () => { setIsClosing(true); setTimeout(onClose, 600); };
    return (
        <div className={`fixed inset-0 z-[100] bg-slate-900 text-white overflow-hidden flex flex-col font-oswald ${isClosing ? 'animate-flip-out' : 'animate-flip-in'}`} onClick={handleClose}>
            <div className="p-6 pt-12 flex flex-col h-full overflow-y-auto no-scrollbar">
                <div className="flex justify-between items-center mb-8">
                    <div className="flex items-center gap-3">
                        <BookOpen size={28} className="text-blue-400"/>
                        <h2 className="text-2xl font-black uppercase tracking-widest">Manual Futsie</h2>
                    </div>
                    <div className="text-xs text-gray-500 uppercase">Toca para cerrar</div>
                </div>
                <div className="space-y-6 pb-20" onClick={e => e.stopPropagation()}>
                    <div className="bg-white/5 p-6 rounded-3xl border border-white/10 backdrop-blur-md">
                        <h3 className="text-lg font-bold text-blue-400 mb-2 flex items-center gap-2"><TrophyIcon size={18}/> ¿Cómo Jugar?</h3>
                        <p className="text-sm font-light text-gray-300 leading-relaxed">Futsie es tu quiniela digital. Únete a una liga con un código o crea la tuya. Pronostica los resultados antes del inicio de cada partido.</p>
                    </div>
                    <div className="bg-white/5 p-6 rounded-3xl border border-white/10 backdrop-blur-md">
                        <h3 className="text-lg font-bold text-green-400 mb-2 flex items-center gap-2"><Star size={18}/> Sistema de Puntuación (General)</h3>
                        <ul className="text-sm font-light text-gray-300 space-y-2">
                            <li><strong className="text-white">+3 Puntos:</strong> Acertar al ganador o empate.</li>
                            <li><strong className="text-white">+2 Puntos Extra:</strong> Acertar al marcador exacto (Total 5).</li>
                        </ul>
                        <div className="mt-4 pt-4 border-t border-white/10">
                            <h4 className="text-sm font-bold text-yellow-400 mb-1 flex items-center gap-1"><TrophyIcon size={14}/> Eliminatorias (Mundial / Copas)</h4>
                            <ul className="text-xs font-light text-gray-300 space-y-1">
                                <li><strong className="text-white">+3 Puntos:</strong> Acertar al equipo ganador (o empate en 90m).</li>
                                <li><strong className="text-white">+2 Puntos Extra:</strong> Acertar al método de definición (90 Minutos, Tiempos Extras o Penales). *No aplica bono de marcador exacto en estas fases.*</li>
                            </ul>
                        </div>
                    </div>
                    <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 p-6 rounded-3xl border border-purple-500/30 relative overflow-hidden backdrop-blur-md">
                        <div className="absolute top-0 right-0 p-4 opacity-20"><ZapIcon size={64}/></div>
                        <h3 className="text-lg font-bold text-purple-400 mb-2 flex items-center gap-2"><ZapIcon size={18}/> Power-Up: Corazonada</h3>
                        <p className="text-sm font-light text-gray-200 leading-relaxed mb-2">¿Estás 100% seguro de un resultado? ¡Usa una Corazonada!</p>
                        <ul className="text-xs font-bold text-white space-y-1">
                            <li>• Costo: 50 FutsieCoins</li>
                            <li>• Beneficio: Multiplica x2 los puntos de ese partido.</li>
                            <li>• Riesgo: Si fallas, pierdes las monedas.</li>
                        </ul>
                    </div>
                    <div className="bg-white/5 p-6 rounded-3xl border border-white/10 backdrop-blur-md">
                        <h3 className="text-lg font-bold text-yellow-400 mb-2 flex items-center gap-2"><Lock size={18}/> Bolsas y Rondas ($)</h3>
                        <p className="text-sm font-light text-gray-300 leading-relaxed mb-3">El Mundial y Ligas mayores se juegan por rondas. El costo por participar en cada ronda (Jornada, Octavos, Cuartos...) es de <strong className="text-white">$100 MXN</strong>.</p>
                        <div className="grid grid-cols-2 gap-2 text-xs font-bold text-gray-400">
                            <div className="bg-black/20 p-2 rounded-lg text-center"><span className="block text-green-400">$50 MXN</span>Al Pozo de la Ronda (Ganador de la jornada)</div>
                            <div className="bg-black/20 p-2 rounded-lg text-center"><span className="block text-yellow-400">$50 MXN</span>Al Pozo Global (Ganador absoluto del torneo)</div>
                        </div>
                    </div>
                    <div className="text-center mt-4">
                        <p className="text-[10px] text-gray-600 uppercase tracking-[0.2em] font-bold">FUTSIE26.1</p>
                    </div>
                </div>
            </div>
            <div className="absolute inset-0 -z-10"></div>
        </div>
    );
};

const StoreView = ({ userProfile, onPurchase }) => (
    <div className="p-6 pb-24 space-y-6 max-w-5xl mx-auto">
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-8 rounded-3xl shadow-xl flex justify-between items-center text-white relative overflow-hidden">
            <div className="relative z-10">
                <h2 className="text-5xl font-black drop-shadow-md mb-1">{userProfile?.inventory?.coins || 0}</h2>
                <p className="text-sm font-bold uppercase tracking-widest opacity-90 border-t border-white/30 pt-2 mt-2 inline-block">Futsie Coins Disponibles</p>
            </div>
            <div className="absolute -right-6 -bottom-8 transform rotate-12 opacity-20"><FutsieCoinIcon className="w-32 h-32"/></div>
        </div>

        <h3 className="font-bold text-gray-600 uppercase tracking-widest text-sm ml-2">Adquirir Monedas</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.values(STORE_ITEMS).filter(i => i.type === 'coins').map(item => (
                <div key={item.id} className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group relative overflow-hidden">
                     <div className="absolute top-0 right-0 p-2 opacity-50 transform scale-150 -rotate-12 -translate-y-2 translate-x-2"><FutsieCoinIcon className="w-16 h-16"/></div>
                    <div className="relative z-10">
                         <div className="mb-4 flex justify-center"><FutsieCoinIcon className="w-16 h-16"/></div>
                        <h4 className="font-black text-gray-800 text-xl mb-1">{item.amount} Coins</h4>
                        <p className="text-sm text-gray-400 mb-4">{item.name}</p>
                        <button onClick={() => onPurchase(item)} className="w-full bg-gray-900 text-white py-3 rounded-2xl text-sm font-bold uppercase tracking-wide hover:bg-black transition-colors shadow-lg group-hover:shadow-xl">
                            ${item.price} MXN
                        </button>
                    </div>
                </div>
            ))}
        </div>

        <h3 className="font-bold text-gray-400 uppercase tracking-widest text-sm ml-2 mt-8">Canjear Power-Ups & Pases</h3>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="space-y-4">
                {Object.values(STORE_ITEMS).filter(i => i.type !== 'coins').map(item => (
                    <div key={item.id} className="flex flex-col sm:flex-row justify-between items-center p-4 bg-gray-50 rounded-2xl border border-gray-100 gap-4">
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-lg shadow-sm border ${item.type === 'powerup' ? 'bg-purple-50 text-purple-600 border-purple-100' : 'bg-yellow-50 text-yellow-600 border-yellow-100'}`}>
                                {item.type === 'powerup' ? <ZapIcon size={24}/> : <Lock size={24}/>}
                            </div>
                            <div>
                                <p className="font-bold text-base text-gray-800">{item.name}</p>
                                <p className="text-sm text-gray-500 max-w-xs">{item.description}</p>
                            </div>
                        </div>
                        <button onClick={() => onPurchase(item)} className="bg-blue-100 text-blue-700 px-6 py-2.5 rounded-full text-xs font-black hover:bg-blue-200 transition-colors whitespace-nowrap uppercase tracking-wide w-full sm:w-auto">
                            {item.price} Coins
                        </button>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const PremiumModal = ({ onClose }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
        <div className="bg-white p-8 rounded-3xl w-full max-w-sm text-center shadow-2xl transform transition-all scale-100 border border-gray-100">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6"><TrophyIcon size={40} className="text-yellow-600"/></div>
            <h2 className="text-2xl font-black mb-3 text-gray-900">Liga Premium 💎</h2>
            <p className="text-gray-500 mb-8 text-sm leading-relaxed">Esta competición requiere un Pase Premium. Puedes adquirirlo en la tienda.</p>
            <button onClick={onClose} className="w-full bg-gray-900 text-white font-bold py-3.5 rounded-xl hover:bg-black transition-colors shadow-xl">Entendido</button>
        </div>
    </div>
);

const MemberCard = ({ member, predictions, fixtures, isWorldCup }) => {
    const [isFlipped, setIsFlipped] = useState(false);

    const stats = useMemo(() => {
        let exact = 0;
        let outcomes = 0;
        const allFixtures = Object.values(fixtures).flat();
        allFixtures.forEach(f => {
            if(['FT', 'AET', 'PEN'].includes(f.fixture.status.short)){
                const pred = predictions[`${member.uid}_${f.fixture.id}`];
                if(pred){
                   const isKnockout = isWorldCup && !f.league.round.toLowerCase().includes('group');
                   const { exact: isExact, outcome: isOutcome } = calculateMatchPoints(f, pred, isKnockout);
                   if(isExact) exact++;
                   if(isOutcome) outcomes++;
                }
            }
        });
        return { exact, outcomes };
    }, [member, predictions, fixtures, isWorldCup]);

    return (
        <div className="relative h-24 w-full perspective-2000 cursor-pointer mb-2" onClick={() => setIsFlipped(!isFlipped)}>
            <div className={`w-full h-full relative transform-style-3d transition-transform duration-500 ${isFlipped ? 'rotate-y-180' : ''}`}>
                <div className="absolute w-full h-full backface-hidden bg-white p-3 rounded-3xl shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition-all">
                    <UserAvatar url={member.photoURL} name={member.name} size="w-14 h-14" fontSize="text-xl" />
                    <div>
                        <p className="font-bold text-gray-800 text-base leading-tight font-light tracking-tight">{member.name}</p>
                        <p className="text-[10px] text-green-600 font-bold uppercase tracking-wider bg-green-50 px-2 py-0.5 rounded-lg inline-block mt-1">
                            Ver Stats
                        </p>
                    </div>
                </div>
                <div className="absolute w-full h-full backface-hidden bg-slate-900 p-3 rounded-3xl shadow-xl text-white rotate-y-180 flex items-center justify-between px-6 border border-white/10">
                    <div className="flex items-center gap-3 border-r border-white/10 pr-4 flex-grow min-w-0 w-1/2">
                         <UserAvatar url={member.photoURL} name={member.name} size="w-10 h-10" />
                        <div className="text-left overflow-hidden">
                            <span className="block font-bold text-sm leading-tight truncate">{member.name}</span>
                        </div>
                    </div>
                    <div className="flex gap-4 text-center flex-shrink-0 w-1/2 justify-center">
                        <div>
                            <span className="block text-xl font-black text-green-400 leading-none">{stats.outcomes}</span>
                            <span className="text-[8px] uppercase tracking-widest text-gray-500 font-bold">Aciertos</span>
                        </div>
                        <div>
                            <span className="block text-xl font-black text-blue-400 leading-none">{stats.exact}</span>
                            <span className="text-[8px] uppercase tracking-widest text-gray-500 font-bold">Extra / Método</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const FixtureCard = ({ fixture, prediction, onSave, isLeaguesCup, isWorldCup, userProfile, onUsePowerUp, competitionKey, isAdmin, onToggleLock, isManuallyUnlocked, isManuallyLocked, liveBroadcaster }) => {
    const [homeScore, setHomeScore] = useState(prediction?.predHomeGoals ?? '');
    const [awayScore, setAwayScore] = useState(prediction?.predAwayGoals ?? '');
    const [penWinner, setPenWinner] = useState(prediction?.predictedPenaltyWinner ?? null);
    const [winMethod, setWinMethod] = useState(prediction?.predictedWinMethod ?? '90MIN'); 
    const [powerUp, setPowerUp] = useState(prediction?.usedPowerUp ?? null);
    const [status, setStatus] = useState(prediction ? 'saved' : 'idle');
    const [isFlipped, setIsFlipped] = useState(false);
    const [closingMethod, setClosingMethod] = useState(null); 
    const [details, setDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const scrollRef = useRef(null);
    const [touchStart, setTouchStart] = useState(null);

    const roundName = fixture.league?.round || "";
    const datePassed = new Date(fixture.fixture.date) < new Date();
    
    const isLocked = isManuallyLocked || (datePassed && !isManuallyUnlocked);
    const isKnockout = isWorldCup && !roundName.toLowerCase().includes('group');
    const predictedOutcome = parseInt(homeScore) > parseInt(awayScore) ? 'home' : (parseInt(awayScore) > parseInt(homeScore) ? 'away' : 'draw');
    const homeNorm = normalizeTeamName(fixture.teams.home.name);
    
    const finalBroadcasters = liveBroadcaster ? String(liveBroadcaster).split(' / ') : (broadcasterMap[homeNorm] || ["TV Local"]);

    const handleClose = useCallback((method = 'flip') => {
        setClosingMethod(method);
        setTimeout(() => {
            setIsFlipped(false);
            setClosingMethod(null);
        }, method === 'slide' ? 500 : 800);
    }, []);

    const handleTouchStart = (e) => { 
        if(scrollRef.current && scrollRef.current.scrollTop <= 5) {
            setTouchStart(e.touches[0].clientY); 
        } else {
            setTouchStart(null); 
        }
    };
    
    const handleTouchMove = (e) => { 
        if (!touchStart) return; 
        const diff = e.touches[0].clientY - touchStart; 
        if (diff > 100) { 
            handleClose('slide'); 
            setTouchStart(null); 
        } 
    };

    useEffect(() => {
        if (isFlipped && !details && !loadingDetails) {
            setLoadingDetails(true);
            const fetchDetails = async () => {
                try {
                    const [pred, lineups, h2h, events, odds, standings] = await Promise.all([
                        fetch(buildProxyUrl(`/predictions?fixture=${fixture.fixture.id}`)),
                        fetch(buildProxyUrl(`/fixtures/lineups?fixture=${fixture.fixture.id}`)),
                        fetch(buildProxyUrl(`/fixtures/headtohead?h2h=${fixture.teams.home.id}-${fixture.teams.away.id}`)),
                        fetch(buildProxyUrl(`/fixtures/events?fixture=${fixture.fixture.id}`)),
                        fetch(buildProxyUrl(`/odds?fixture=${fixture.fixture.id}`)),
                        fetch(buildProxyUrl(`/standings?league=${fixture.league.id}&season=${fixture.league.season}`))
                    ]);
                    const [dPred, dLin, dH2h, dEv, dOdds, dStandings] = await Promise.all([pred.json(), lineups.json(), h2h.json(), events.json(), odds.json(), standings.json()]);

                    setDetails({
                        prediction: dPred.response?.[0] || null,
                        lineups: dLin.response || [],
                        h2h: (dH2h.response || []).sort((a,b) => new Date(b.fixture.date) - new Date(a.fixture.date)),
                        events: dEv.response || [],
                        odds: dOdds.response?.[0]?.bookmakers || [],
                        standings: dStandings.response?.[0]?.league?.standings?.[0] || []
                    });
                } catch (e) { console.error(e); } finally { setLoadingDetails(false); }
            };
            fetchDetails();
        }
    }, [isFlipped, fixture.fixture.id, details, loadingDetails, fixture.teams.home.id, fixture.teams.away.id, fixture.league.id, fixture.league.season]);

    useEffect(() => {
        setHomeScore(prediction?.predHomeGoals ?? '');
        setAwayScore(prediction?.predAwayGoals ?? '');
        setPenWinner(prediction?.predictedPenaltyWinner ?? null);
        setWinMethod(prediction?.predictedWinMethod ?? '90MIN');
        setPowerUp(prediction?.usedPowerUp ?? null);
        setStatus(prediction ? 'saved' : 'idle');
    }, [prediction]);

    const handleSave = async (e) => {
        e.stopPropagation();
        if (homeScore === '' || awayScore === '') return;
        if (isLeaguesCup && parseInt(homeScore) === parseInt(awayScore) && !penWinner) return;
        if (powerUp && powerUp !== prediction?.usedPowerUp) { if (!(await onUsePowerUp(powerUp))) return; }
        setStatus('saving');
        await onSave({
            fixtureId: fixture.fixture.id,
            predHomeGoals: parseInt(homeScore),
            predAwayGoals: parseInt(awayScore),
            predictedOutcome: parseInt(homeScore) > parseInt(awayScore) ? 'home' : (parseInt(awayScore) > parseInt(homeScore) ? 'away' : 'draw'),
            predictedPenaltyWinner: penWinner,
            predictedWinMethod: isKnockout ? winMethod : null,
            usedPowerUp: powerUp
        });
        setStatus('saved');
    };

    const getTeamClasses = (team) => {
        let classes = `flex flex-col items-center p-3 rounded-2xl transition-all duration-200 w-1/3 cursor-pointer select-none border-2`;
        if (isLocked) classes += ' bg-gray-50 border-gray-100 opacity-60 cursor-default';
        else if (isLeaguesCup && predictedOutcome === 'draw') classes += ' hover:bg-yellow-50 border-transparent hover:border-yellow-200';
        else classes += ' hover:bg-gray-50 border-transparent hover:border-blue-100';
        if (predictedOutcome === team) classes += ' bg-blue-50 border-blue-500 !opacity-100 shadow-md transform scale-105';
        else if (isLeaguesCup && predictedOutcome === 'draw' && penWinner === team) classes += ' bg-yellow-50 border-yellow-500 !opacity-100 shadow-md scale-105';
        return classes;
    };

    const renderEvent = (ev) => (
         <div key={`${ev.time.elapsed}-${ev.player.id}`} className={`flex items-center gap-2 text-xs mb-1 ${ev.team.id === fixture.teams.home.id ? 'flex-row' : 'flex-row-reverse text-right'}`}>
            <span className="font-mono text-gray-500 w-6 text-center">{ev.time.elapsed}'</span>
            <span className="flex items-center gap-1">
                {ev.type === 'Goal' ? "⚽" : ev.type === 'Card' ? (ev.detail.includes("Yellow") ? <div className="w-3 h-4 bg-yellow-400 rounded-sm"/> : <div className="w-3 h-4 bg-red-600 rounded-sm"/>) : <RefreshCw size={14} className="text-green-400"/>}
                <span className={ev.type === 'Goal' ? "text-white" : "text-gray-300"}>{ev.player.name}</span>
            </span>
        </div>
    );

    const isToday = new Date(fixture.fixture.date).setHours(0,0,0,0) === new Date().setHours(0,0,0,0);
    const dateDisplay = isToday ? "HOY" : new Date(fixture.fixture.date).toLocaleDateString('es-MX', {weekday:'short', day:'numeric'});
    const dateClass = isToday ? "text-gray-800 font-black text-xs" : "text-gray-400 text-[10px] font-bold";

    const getSuggestion = (bookmakers) => {
        if (!bookmakers || bookmakers.length === 0) return null;
        const bets = bookmakers[0]?.bets?.[0]?.values;
        if (!bets) return null;
        const homeOdd = parseFloat(bets.find(v => v.value === "Home")?.odd || 0);
        const awayOdd = parseFloat(bets.find(v => v.value === "Away")?.odd || 0);
        if (homeOdd > 0 && awayOdd > 0) {
            if (homeOdd < awayOdd - 0.5) return "Los números se inclinan ligeramente hacia el Local.";
            if (awayOdd < homeOdd - 0.5) return "Los números favorecen ligeramente a la Visita.";
            return "Duelo muy cerrado, el empate es una posibilidad alta.";
        }
        return null;
    };

    const getTeamStats = (teamId) => {
        if (!details || !details.standings) return null;
        const team = details.standings.find(s => s.team.id === teamId);
        if (!team) return null;
        return {
            rank: team.rank,
            points: team.points,
            gf: team.all.goals.for,
            ga: team.all.goals.against,
            form: team.form
        };
    };

    const renderTeamStatsRow = (stats, teamName) => (
        <div className="flex justify-between items-center text-xs border-b border-white/5 pb-2 mb-2 last:mb-0 last:pb-0 last:border-0">
            <span className="font-bold text-gray-300 w-1/3 truncate">{teamName}</span>
            <div className="flex gap-3 text-center w-2/3 justify-end">
                <div className="w-8"><span className="block text-gray-500 text-[8px] uppercase">Pos</span><span className="font-black text-white">{stats.rank}</span></div>
                <div className="w-8"><span className="block text-gray-500 text-[8px] uppercase">Pts</span><span className="font-black text-yellow-400">{stats.points}</span></div>
                <div className="w-8"><span className="block text-gray-500 text-[8px] uppercase">GF</span><span className="font-medium text-gray-300">{stats.gf}</span></div>
                <div className="w-8"><span className="block text-gray-500 text-[8px] uppercase">GC</span><span className="font-medium text-gray-300">{stats.ga}</span></div>
                <div className="w-16"><span className="block text-gray-500 text-[8px] uppercase mb-0.5">Forma</span><div className="flex gap-0.5 justify-center">{stats.form?.split('').map((r,i) => <span key={i} className={`w-2 h-2 rounded-full ${r==='W'?'bg-green-500':r==='D'?'bg-gray-500':'bg-red-500'}`}></span>)}</div></div>
            </div>
        </div>
    );

    if (isFlipped || closingMethod) {
        return (
            <div
                className={`fixed inset-0 z-[100] bg-slate-950 text-white overflow-hidden flex flex-col font-oswald ${closingMethod === 'slide' ? 'animate-slide-down-out' : (closingMethod === 'flip' ? 'animate-flip-out' : 'animate-flip-in')}`}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onClick={() => handleClose('flip')}
            >
                <div className="bg-slate-900/80 p-4 pt-12 md:pt-6 flex justify-between items-center border-b border-white/10" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white rounded-full p-1 flex items-center justify-center"><CompetitionLogo competitionKey={competitionKey} className="w-full h-full object-contain" /></div>
                        <div><h3 className="font-bold text-sm text-white uppercase tracking-wider">{COMPETITIONS[competitionKey]?.name}</h3><p className="text-[10px] text-blue-400 font-bold">{formatRoundName(fixture.league.round)}</p></div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto no-scrollbar pb-20" ref={scrollRef} onClick={e => e.stopPropagation()}>
                    <div className="sticky top-0 z-20 bg-slate-950/95 backdrop-blur-md border-b border-white/10 py-6 px-4 shadow-2xl">
                         <div className="flex justify-between items-center max-w-lg mx-auto">
                             <div className="flex flex-col items-center w-1/3">
                                 <img src={fixture.teams.home.logo} className="w-20 h-20 object-contain drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]" alt="H"/>
                                 <h2 className="font-bold text-lg text-center mt-2 leading-tight uppercase tracking-tight">{fixture.teams.home.name}</h2>
                             </div>
                             <div className="flex flex-col items-center justify-center w-1/3 text-center">
                                <div className={`${dateClass} uppercase tracking-widest mb-1`}>{dateDisplay}</div>
                                <span className="text-4xl font-black text-white tracking-tighter">{['FT', 'AET', 'PEN', '1H', '2H', 'HT', 'ET', 'LIVE'].includes(fixture.fixture.status.short) ? `${fixture.goals.home} - ${fixture.goals.away}` : "VS"}</span>
                                <div className="mt-1 px-3 py-0.5 rounded-full bg-blue-600/20 border border-blue-500/30 text-[10px] font-bold text-blue-400 flex flex-col items-center"><span>{new Date(fixture.fixture.date).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span></div>
                            </div>
                            <div className="flex flex-col items-center w-1/3">
                                <img src={fixture.teams.away.logo} className="w-20 h-20 object-contain drop-shadow-[0_0_15px_rgba(239,68,68,0.5)]" alt="A"/>
                                <h2 className="font-bold text-lg text-center mt-2 leading-tight uppercase tracking-tight">{fixture.teams.away.name}</h2>
                            </div>
                        </div>
                    </div>

                    <div className="p-4 space-y-8 max-w-lg mx-auto mt-4">
                        <div className="flex justify-center gap-4 text-center">
                             <div className="bg-white/5 px-4 py-3 rounded-lg border border-white/5 w-1/2"><p className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">Estadio</p><p className="font-bold text-xs truncate text-gray-200">🏟️ {fixture.fixture.venue?.name || "TBD"}</p></div>
                             <div className="bg-white/5 px-4 py-3 rounded-lg border border-white/5 w-1/2"><p className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">Árbitro</p><p className="font-bold text-xs truncate text-gray-200 flex items-center justify-center gap-1"><WhistleIcon size={12} className="text-yellow-400"/> {fixture.fixture.referee || "Por definir"}</p></div>
                        </div>

                        {loadingDetails ? <div className="py-20 text-center text-gray-500 animate-pulse">Cargando estadísticas en tiempo real...</div> : details ? (
                           <>
                                {details.events && details.events.length > 0 && <div className="bg-white/5 p-4 rounded-2xl border border-white/5"><h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center justify-center gap-2"><Activity size={12}/> Minuto a Minuto</h4><div className="space-y-1">{details.events.map(ev => renderEvent(ev))}</div></div>}

                                <div className="space-y-4">
                                     <h4 className="text-sm font-black text-green-400 uppercase tracking-widest flex items-center gap-2 border-b border-green-500/20 pb-2"><TrendingUp size={16}/> Momios & Probabilidades</h4>
                                     {details.odds && details.odds.length > 0 ? (
                                          <div className="space-y-3">
                                              {details.odds.slice(0, 3).map((bookie, idx) => (
                                                   <div key={idx} className="bg-white/5 p-3 rounded-xl border border-white/5">
                                                       <p className="text-[10px] text-gray-400 mb-1 uppercase tracking-wide font-bold">{bookie.name}</p>
                                                       <div className="flex justify-between text-xs font-bold">
                                                          <span className="text-green-400">1: <span className="text-white">{bookie.bets[0].values.find(v=>v.value==='Home')?.odd}</span></span>
                                                          <span className="text-gray-400">X: <span className="text-white">{bookie.bets[0].values.find(v=>v.value==='Draw')?.odd}</span></span>
                                                          <span className="text-red-400">2: <span className="text-white">{bookie.bets[0].values.find(v=>v.value==='Away')?.odd}</span></span>
                                                       </div>
                                                   </div>
                                              ))}
                                              <p className="text-xs text-gray-500 italic mt-2 text-center border-t border-white/5 pt-2">{getSuggestion(details.odds)}</p>
                                          </div>
                                      ) : <p className="text-xs text-gray-500 text-center">Momios no disponibles aún.</p>}

                                      {details.prediction?.predictions?.percent && <div className="space-y-6 bg-white/5 p-6 rounded-2xl border border-white/5"><p className="text-[10px] text-blue-400 mb-0 uppercase tracking-wide font-bold flex items-center gap-1"><Sparkles size={12}/> Análisis Gemini IA</p><div><div className="flex justify-between text-xs font-bold mb-1 uppercase tracking-wider"><span className="text-gray-300">{fixture.teams.home.name}</span><span className="text-white">{details.prediction.predictions.percent.home}</span></div><div className="h-3 bg-gray-800 rounded-full overflow-hidden"><div style={{width: details.prediction.predictions.percent.home}} className="h-full bg-blue-500 shadow-[0_0_10px_#3b82f6]"></div></div></div><div><div className="flex justify-between text-xs font-bold mb-1 uppercase tracking-wider"><span className="text-gray-400">Empate</span><span className="text-white">{details.prediction.predictions.percent.draw}</span></div><div className="h-3 bg-gray-800 rounded-full overflow-hidden"><div style={{width: details.prediction.predictions.percent.draw}} className="h-full bg-gray-500"></div></div></div><div><div className="flex justify-between text-xs font-bold mb-1 uppercase tracking-wider"><span className="text-gray-300">{fixture.teams.away.name}</span><span className="text-white">{details.prediction.predictions.percent.away}</span></div><div className="h-3 bg-gray-800 rounded-full overflow-hidden"><div style={{width: details.prediction.predictions.percent.away}} className="h-full bg-red-500 shadow-[0_0_10px_#ef4444]"></div></div></div></div>}
                                </div>
                                
                                <div><h4 className="text-sm font-black text-blue-400 uppercase tracking-widest flex items-center gap-2 border-b border-blue-500/20 pb-2 mb-4"><Activity size={16}/> Historial H2H (Últimos 5)</h4>{details.h2h && details.h2h.length > 0 ? <div className="space-y-2">{details.h2h.slice(0, 5).map((m, idx) => (<div key={idx} className="bg-white/5 p-3 rounded-lg flex justify-between items-center text-xs border border-white/5"><span className="text-gray-400 w-16">{new Date(m.fixture.date).toLocaleDateString()}</span><div className="flex-1 text-center font-bold flex justify-center gap-3"><span className={m.teams.home.winner ? 'text-green-400' : 'text-gray-300'}>{m.teams.home.name}</span><span className="bg-black/40 px-2 py-0.5 rounded text-white">{m.goals.home} - {m.goals.away}</span><span className={m.teams.away.winner ? 'text-green-400' : 'text-gray-300'}>{m.teams.away.name}</span></div></div>))}</div> : <div className="text-center text-gray-500 text-xs py-4">Sin historial reciente disponible.</div>}</div>
                                
                                {details.standings && details.standings.length > 0 && (
                                    <div className="bg-white/5 p-4 rounded-2xl border border-white/5">
                                        <h4 className="text-sm font-black text-yellow-400 uppercase tracking-widest flex items-center gap-2 border-b border-yellow-500/20 pb-2 mb-3"><TrophyIcon size={16}/> Posiciones en la Tabla</h4>
                                        <div className="space-y-2">
                                            {renderTeamStatsRow(getTeamStats(fixture.teams.home.id) || {rank:'-',points:'-',gf:'-',ga:'-',form:''}, fixture.teams.home.name)}
                                            {renderTeamStatsRow(getTeamStats(fixture.teams.away.id) || {rank:'-',points:'-',gf:'-',ga:'-',form:''}, fixture.teams.away.name)}
                                        </div>
                                    </div>
                                )}

                                <div><h4 className="text-sm font-black text-purple-400 uppercase tracking-widest flex items-center gap-2 border-b border-purple-500/20 pb-2 mb-4"><Users size={16}/> Alineaciones</h4>{details.lineups && details.lineups.length >= 2 ? <div className="flex justify-between text-xs bg-white/5 p-4 rounded-xl border border-white/5"><ul className="space-y-2 text-left w-1/2 pr-2 border-r border-white/10"><li className="font-bold text-blue-300 mb-2 uppercase tracking-wide">{details.lineups[0].team.name}</li>{details.lineups[0].startXI.map((p, i) => <li key={i} className="text-gray-300 truncate font-medium">{p.player.number}. {p.player.name}</li>)}</ul><ul className="space-y-2 text-right w-1/2 pl-2"><li className="font-bold text-red-300 mb-2 uppercase tracking-wide">{details.lineups[1].team.name}</li>{details.lineups[1].startXI.map((p, i) => <li key={i} className="text-gray-300 truncate font-medium">{p.player.name} .{p.player.number}</li>)}</ul></div> : <div className="bg-white/5 border border-white/10 border-dashed rounded-2xl p-6 text-center"><Users size={24} className="mx-auto text-gray-600 mb-2"/><p className="text-gray-400 font-bold text-xs uppercase tracking-wider">Alineaciones no disponibles</p></div>}</div>
                           </>
                        ) : <div className="py-10 text-center"><p className="text-gray-500 font-bold uppercase tracking-widest text-xs">Información detallada no disponible.</p></div>}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`relative w-full min-h-[280px] perspective-2000 mb-4 animate-fade-in`}
            onClick={() => setIsFlipped(true)}
        >
            <div className={`w-full bg-white/70 backdrop-blur-xl p-5 rounded-3xl shadow-sm border border-white/50 relative transition-all hover:shadow-md overflow-hidden ${isLocked ? 'grayscale-[0.1]' : ''}`}>
                 <div className="absolute top-0 right-0 p-3">
                     <Info size={18} className="text-blue-400 hover:text-blue-600 cursor-pointer"/>
                </div>

                {isAdmin && (
                    <div
                        className="absolute top-0 left-0 p-3 z-10"
                        onClick={(e) => { e.stopPropagation(); onToggleLock(fixture.fixture.id, isLocked); }}
                    >
                        {isLocked && !isManuallyLocked ? (
                             <Lock size={18} className="text-gray-400 hover:text-gray-600 cursor-pointer" />
                        ) : isManuallyLocked ? (
                             <Lock size={18} className="text-red-500 hover:text-red-600 cursor-pointer" />
                        ) : (
                             <Unlock size={18} className="text-green-500 hover:text-green-600 cursor-pointer" />
                        )}
                    </div>
                )}

                <div className="flex flex-col items-center justify-center mb-6 text-sm font-bold text-gray-500 uppercase tracking-wider text-center gap-1">
                    <span className="text-gray-400 text-[10px] tracking-[0.2em]">{formatRoundName(fixture.league.round)}</span>
                    <span className={dateClass}>{dateDisplay} • {new Date(fixture.fixture.date).toLocaleTimeString('es-MX', {hour:'2-digit', minute:'2-digit'})}</span>
                    <span className="flex items-center gap-1 text-[11px] text-blue-600 font-black normal-case mt-1 bg-blue-50 px-2 py-0.5 rounded-full"><Tv size={12}/> {finalBroadcasters.join(', ')}</span>
                </div>
                 <div className="flex flex-row items-center justify-between gap-1 mb-4">
                    <div className={getTeamClasses('home')} onClick={(e) => { if(!isLocked && isLeaguesCup && predictedOutcome === 'draw') { e.stopPropagation(); setPenWinner('home'); } }}>
                        <img src={fixture.teams.home.logo} className="w-16 h-16 mb-2 object-contain drop-shadow-sm" alt="H"/>
                        <span className="text-xs font-black text-center leading-tight text-gray-800 h-8 flex items-center justify-center w-full line-clamp-2 font-light tracking-tight">{fixture.teams.home.name}</span>
                        {predictedOutcome === 'home' && <div className="mt-1 bg-blue-500 text-white rounded-full p-0.5"><CheckIcon size={12}/></div>}
                    </div>
                    <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <input type="tel" value={homeScore} disabled={isLocked} onChange={e => { setHomeScore(e.target.value); setStatus('idle'); }} className="w-12 h-14 text-center font-black text-3xl bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none shadow-inner transition-all text-gray-900 placeholder-gray-300" placeholder="-"/>
                        <span className="text-gray-300 font-black text-xl mx-1">-</span>
                        <input type="tel" value={awayScore} disabled={isLocked} onChange={e => { setAwayScore(e.target.value); setStatus('idle'); }} className="w-12 h-14 text-center font-black text-3xl bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none shadow-inner transition-all text-gray-900 placeholder-gray-300" placeholder="-"/>
                    </div>
                    <div className={getTeamClasses('away')} onClick={(e) => { if(!isLocked && isLeaguesCup && predictedOutcome === 'draw') { e.stopPropagation(); setPenWinner('away'); } }}>
                        <img src={fixture.teams.away.logo} className="w-16 h-16 mb-2 object-contain drop-shadow-sm" alt="A"/>
                        <span className="text-xs font-black text-center leading-tight text-gray-800 h-8 flex items-center justify-center w-full line-clamp-2 font-light tracking-tight">{fixture.teams.away.name}</span>
                        {predictedOutcome === 'away' && <div className="mt-1 bg-blue-500 text-white rounded-full p-0.5"><CheckIcon size={12}/></div>}
                    </div>
                </div>

                {isKnockout && (
                    <div className="mb-4 bg-gray-50 p-3 rounded-2xl border border-gray-100 text-center" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">¿Cómo se define?</span>
                        <div className="flex justify-center gap-2">
                            {['90MIN', 'ET', 'PEN'].map(method => (
                                <button
                                    key={method}
                                    disabled={isLocked}
                                    onClick={() => { setWinMethod(method); setStatus('idle'); }}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${winMethod === method ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-100'} ${isLocked ? 'opacity-50 cursor-default' : ''}`}
                                >
                                    {method === '90MIN' ? '90 Min' : method === 'ET' ? 'Tiempos Extra' : 'Penales'}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {!isLocked && (
                    <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
                        <button onClick={handleSave} disabled={status !== 'idle'} className={`w-full py-3 rounded-2xl font-black text-xs uppercase tracking-wider transition-all shadow-lg active:scale-95 transform ${status === 'saved' ? 'bg-green-500 text-white shadow-green-200' : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-blue-200'}`}>
                            {status === 'saved' ? <span className="flex items-center justify-center gap-2"><CheckIcon size={16}/> Guardado</span> : 'Guardar Pronóstico'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const ScrollToTopFAB = ({ onClick }) => (
    <button onClick={onClick} className="fixed bottom-24 right-5 z-40 bg-blue-600 text-white w-14 h-14 rounded-full shadow-2xl flex items-center justify-center border-4 border-white/20 hover:scale-105 transition-all hover:bg-blue-700" aria-label="Volver arriba"><ArrowUp size={28} /></button>
);

const QuinielaHeader = ({ groupName, onExitGroup, competitionName, competitionKey }) => (
    <div className="bg-slate-900 border-b border-white/10 sticky top-0 z-40 shadow-lg flex-none">
        <div className="max-w-7xl mx-auto p-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
                 <button onClick={onExitGroup} className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-colors flex items-center justify-center"><ArrowLeft size={24} /></button>
                <div className="flex flex-col">
                    <h1 className="text-xl md:text-2xl font-black text-white leading-tight tracking-tight font-wc">{groupName}</h1>
                </div>
            </div>
            <div><FutsieLogo scale={0.35} light /></div>
        </div>
    </div>
);

const QuinielaNav = ({ activeView, setActiveView }) => {
    const buttons = [
        { key: 'arena', label: 'ARENA', icon: MessageSquare },
        { key: 'pronosticos', label: 'JORNADAS', icon: Calendar },
        { key: 'resultados', label: 'RESULTADOS', icon: CheckIcon },
        { key: 'tabla', label: 'POSICIONES', icon: TrophyIcon },
        { key: 'pagos', label: 'PAGOS', icon: DollarSign},
        { key: 'tienda', label: 'TIENDA', icon: ShoppingBag},
        { key: 'participantes', label: 'MIEMBROS', icon: Users }
    ];
    const topRow = buttons.slice(0, 4);
    const bottomRow = buttons.slice(4);
    return (
        <div className="bg-white border-b border-gray-200 shadow-sm z-30 sticky top-0 flex-none">
            <div className="max-w-7xl mx-auto px-2 py-2">
                <div className="flex justify-around mb-2 border-b border-gray-100 pb-2">{topRow.map(btn => {
                    const Icon = btn.icon;
                    return (
                        <button key={btn.key} onClick={() => setActiveView(btn.key)} className={`text-[10px] font-bold uppercase tracking-wider flex flex-col items-center gap-1 transition-colors ${activeView === btn.key ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}><Icon size={18} />{btn.label}</button>
                    )
                })}</div>
                <div className="flex justify-around">{bottomRow.map(btn => {
                    const Icon = btn.icon;
                    return (
                        <button key={btn.key} onClick={() => setActiveView(btn.key)} className={`text-[10px] font-bold uppercase tracking-wider flex flex-col items-center gap-1 transition-colors ${activeView === btn.key ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}><Icon size={18} />{btn.label}</button>
                    )
                })}</div>
            </div>
        </div>
    );
};

const JornadaNav = ({ jornadas, selectedJornada, onSelectJornada }) => (
    <div className="bg-gray-50/95 border-b border-gray-200 py-3 shadow-sm z-20 flex-none">
        <div className="max-w-7xl mx-auto px-4 flex gap-2 overflow-x-auto no-scrollbar">
            {jornadas.map(j => (<button key={j} onClick={() => onSelectJornada(j)} className={`whitespace-nowrap px-5 py-2 rounded-full text-xs font-bold transition-all ${selectedJornada === j ? 'bg-gray-900 text-white shadow-lg transform scale-105' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-100 hover:border-gray-300'}`}>{shortRoundName(j)}</button>))}
        </div>
    </div>
);

const ResultsView = ({ fixtures, predictions, participants, selectedJornada, isWorldCup }) => {
    const currentFixtures = fixtures[selectedJornada] || [];
    const sortedFixtures = [...currentFixtures].sort((a,b) => new Date(a.fixture.date) - new Date(b.fixture.date));
    
    return (
        <div className="flex flex-col w-full pb-32 relative">
            <div className="p-4 space-y-4 max-w-3xl mx-auto w-full pt-4">
                {sortedFixtures.length === 0 && <div className="bg-white rounded-xl p-12 text-center text-gray-400 border border-gray-200 shadow-sm"><TrophyIcon size={64} className="mx-auto mb-4 opacity-50"/><p className="text-lg font-bold">No hay partidos en esta ronda.</p></div>}
                
                <div className="flex flex-col gap-4">
                    {sortedFixtures.map(f => {
                        const matchDate = new Date(f.fixture.date);
                        const matchStarted = new Date() >= matchDate;
                        const isFinished = ['FT', 'AET', 'PEN'].includes(f.fixture.status.short);
                        const isLive = ['1H', 'HT', '2H', 'ET', 'P', 'BT', 'LIVE'].includes(f.fixture.status.short);
                        
                        const statusColor = isFinished ? 'bg-gray-100 text-gray-900' : (isLive ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-blue-50 text-blue-600');
                        const statusText = isFinished || isLive ? `${f.goals.home ?? 0} - ${f.goals.away ?? 0}` : 'VS';

                        return (
                            <div key={f.fixture.id} className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
                                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
                                    <div className="flex flex-col items-center w-1/3 gap-1 min-w-0"><img src={f.teams.home.logo} className="w-12 h-12 object-contain" alt="H"/> <span className="font-bold text-xs text-gray-800 text-center leading-tight font-light tracking-tight truncate w-full">{f.teams.home.name}</span></div>
                                    <div className={`px-4 py-2 rounded-xl font-black text-xl sm:text-3xl tracking-tighter whitespace-nowrap ${statusColor}`}>{statusText}</div>
                                    <div className="flex flex-col items-center w-1/3 gap-1 min-w-0"><img src={f.teams.away.logo} className="w-12 h-12 object-contain" alt="A"/><span className="font-bold text-xs text-gray-800 text-center leading-tight font-light tracking-tight truncate w-full">{f.teams.away.name}</span></div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    {participants.map(p => {
                                        const pred = predictions[`${p.uid}_${f.fixture.id}`];
                                        let bgClass = 'bg-gray-50'; 
                                        let checkIcon = null;
                                        let timeStr = '';
                                        let isLate = false;

                                        if (pred) {
                                            if (pred.submittedAt) {
                                                const pDate = new Date(pred.submittedAt);
                                                timeStr = pDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) + ' ' + pDate.toLocaleDateString([], {day:'2-digit', month:'2-digit'});
                                                isLate = pDate > matchDate;
                                            }

                                            if (isFinished) {
                                                const isKnockout = isWorldCup && !f.league.round.toLowerCase().includes('group');
                                                const stats = calculateMatchPoints(f, pred, isKnockout);
                                                if (stats.total === 5) { bgClass = 'bg-blue-50'; checkIcon = <CheckCheck size={18} className="text-blue-600 stroke-[3]" />; }
                                                else if (stats.total >= 3) { bgClass = 'bg-green-50'; checkIcon = <CheckIcon size={18} className="text-green-700 stroke-[3]" />; }
                                                else { checkIcon = <X size={18} className="text-red-400 stroke-[3]" />; }
                                            }
                                        }

                                        return (
                                            <div key={p.uid} className={`flex justify-between items-center text-sm p-3 rounded-xl transition-colors ${bgClass}`}>
                                                <div className="flex items-center gap-3">
                                                    <UserAvatar url={p.photoURL} name={p.name} size="w-8 h-8" />
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-gray-700 font-light tracking-tight flex items-center gap-1">
                                                            {p.name}
                                                            {isLate && <span className="text-red-600 text-lg leading-none" title="Pronóstico ingresado después del pitazo inicial">*</span>}
                                                        </span>
                                                        <span className="text-[9px] text-gray-400">{timeStr || 'Sin registro de hora'}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    {isFinished || matchStarted ? (
                                                        <span className="text-gray-500 font-mono tracking-widest">{pred ? `${pred.predHomeGoals}-${pred.predAwayGoals}` : '-'}</span>
                                                    ) : (
                                                        <span className="text-gray-400 italic text-[10px] tracking-widest bg-gray-200 px-2 py-1 rounded-md">{pred ? '🔒 Oculto' : '-'}</span>
                                                    )}
                                                    
                                                    {isFinished && pred && (
                                                        <div className="flex items-center gap-2">
                                                            {checkIcon}
                                                            <span className={`font-black text-base ${calculateMatchPoints(f, pred, isWorldCup && !f.league.round.toLowerCase().includes('group')).total > 0 ? 'text-green-600' : 'text-gray-300'}`}>
                                                                +{calculateMatchPoints(f, pred, isWorldCup && !f.league.round.toLowerCase().includes('group')).total}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <AppFooter />
        </div>
    );
};

const LeaderboardView = ({ fixtures, predictions, participants, payments, selectedJornada, isWorldCup }) => {
    const rounds = useMemo(() => {
        const r = new Set();
        fixtures.forEach(f => r.add(f.league.round));
        return Array.from(r).sort((a,b) => parseInt(a.replace(/\D/g,'')) - parseInt(b.replace(/\D/g,'')));
    }, [fixtures]);
    const ranking = useMemo(() => {
        return participants.map(p => {
            let total = 0; const roundPts = {};
            fixtures.forEach(f => {
                if (['FT', 'AET', 'PEN'].includes(f.fixture.status.short)) {
                    const pred = predictions[`${p.uid}_${f.fixture.id}`];
                    const isKnockout = isWorldCup && !f.league.round.toLowerCase().includes('group');
                    const pts = calculateMatchPoints(f, pred, isKnockout).total;
                    total += pts; const rName = f.league.round;
                    roundPts[rName] = (roundPts[rName] || 0) + pts;
                }
            });
            return { ...p, total, roundPts };
        }).sort((a,b) => b.total - a.total);
    }, [fixtures, predictions, participants, isWorldCup]);

    const totalPaidAllRounds = useMemo(() => {
        if(!payments) return 0;
        return Object.values(payments).flatMap(u => Object.values(u)).filter(s => s === 'admin_confirmed').length;
    }, [payments]);

    const totalPaidThisRound = useMemo(() => {
        if(!payments || !selectedJornada) return 0;
        const key = selectedJornada.replace(/\s/g, '_');
        return Object.values(payments).map(u => u[key]).filter(s => s === 'admin_confirmed').length;
    }, [payments, selectedJornada]);

    const globalPot = totalPaidAllRounds * 50;
    const roundPot = totalPaidThisRound * 50;

    return (
        <div className="p-4 pb-24 max-w-7xl mx-auto w-full pt-8">
            <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-2xl p-4 text-white shadow-lg border border-yellow-300">
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-80 block mb-1">Bolsa Global (Absoluto)</span>
                    <span className="text-3xl font-black drop-shadow-md">${globalPot}</span>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-emerald-700 rounded-2xl p-4 text-white shadow-lg border border-green-400">
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-80 block mb-1">Bolsa Ronda Actual</span>
                    <span className="text-3xl font-black drop-shadow-md">${roundPot}</span>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200 text-sm font-bold text-gray-500 uppercase tracking-widest">
                            <tr><th className="p-4 text-center sticky left-0 bg-gray-50 z-10 w-16">Pos</th><th className="p-4 text-left sticky left-16 bg-gray-50 z-10 w-48 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">Jugador</th><th className="p-4 text-center bg-blue-50 text-blue-800">Total</th>{rounds.map(r => <th key={r} className="p-4 text-center whitespace-nowrap min-w-[80px]">{shortRoundName(r)}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-sm">
                            {ranking.map((r, i) => (
                                <tr key={r.uid} className={`hover:bg-blue-50/50 transition-colors ${i===0 ? 'bg-yellow-50/30' : ''}`}>
                                    <td className="p-4 text-center sticky left-0 bg-white font-bold text-gray-500 border-r border-gray-100">{i === 0 ? '🥇' : (i === 1 ? '🥈' : (i === 2 ? '🥉' : i + 1))}</td>
                                    <td className="p-4 font-bold text-gray-800 sticky left-16 bg-white border-r border-gray-100 flex items-center gap-2 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"><UserAvatar url={r.photoURL} name={r.name} /><span className="font-light tracking-tight">{r.name}</span></td>
                                    <td className="p-4 text-center font-black text-xl text-blue-600 bg-blue-50/30">{r.total}</td>
                                    {rounds.map(rd => (<td key={rd} className="p-4 text-center text-gray-600 font-medium">{r.roundPts[rd] || '-'}</td>))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <AppFooter />
        </div>
    );
};

const ControlPagosView = ({ participants, jornadas, payments, onUpdate, user, isAdmin, group }) => {
    const [info, setInfo] = useState(group.depositInfo || "");
    const [edit, setEdit] = useState(false);
    
    const fullJornadas = [...jornadas];

    const toggle = (uid, j) => {
        const key = j.replace(/\s/g, '_');
        const current = payments[uid]?.[key];
        let next = null;
        if (isAdmin) next = current === 'admin_confirmed' ? null : 'admin_confirmed';
        else { if (uid !== user.uid || current === 'admin_confirmed') return; next = current === 'user_paid' ? null : 'user_paid'; }
        if (next !== undefined) onUpdate(uid, j, next);
    };
    const saveInfo = async () => { await updateDoc(doc(db, 'groups', group.id), { depositInfo: info }); setEdit(false); };
    const parseDepositInfo = (text) => { if (!text) return { number: "0000 0000 0000 0000", details: "" }; const lines = text.split('\n'); return { number: lines[0], details: lines.slice(1).join('\n') }; };
    const displayInfo = parseDepositInfo(info);
    const formatCardNumber = (num) => num.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim();
    const copyInfo = () => { 
        try { navigator.clipboard.writeText(displayInfo.number); console.log("Copiado al portapapeles"); } 
        catch (e) { console.error("Error copiando", e); } 
    };

    return (
        <div className="p-6 space-y-8 pb-24 max-w-7xl mx-auto pt-8">
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-8 rounded-3xl shadow-2xl relative overflow-hidden border border-white/10 group">
                <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity duration-500 transform scale-150 origin-top-right"><CreditCard size={200} /></div>
                <div className="relative z-10">
                    <div className="flex justify-between items-start mb-8">
                        <div><h3 className="font-black text-2xl text-white mb-1 flex items-center gap-3"><DollarSign className="text-green-400"/> Datos de Depósito ($100 MXN x Ronda)</h3><p className="text-white/50 text-sm">El pago debe confirmarse antes de que inicie la ronda.</p></div>
                        {isAdmin && !edit && <button onClick={() => setEdit(true)} className="text-xs bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-colors font-bold uppercase tracking-wider">Editar Info</button>}
                    </div>
                    {edit ? (
                        <div className="space-y-4 bg-black/30 p-6 rounded-2xl border border-white/10">
                             <p className="text-xs text-gray-400 mb-1">Línea 1: Número de Cuenta. Líneas siguientes: Detalles.</p>
                            <textarea value={info} onChange={e => setInfo(e.target.value)} className="w-full bg-transparent border-b-2 border-white/20 p-2 text-lg text-white placeholder-white/20 outline-none focus:border-green-400 transition-colors font-mono" rows={4} placeholder="1234567890123456\nBanco Azteca\nJuan Pérez"/>
                            <div className="flex gap-3 justify-end"><button onClick={() => setEdit(false)} className="text-sm text-gray-400 hover:text-white px-4 font-bold">Cancelar</button><button onClick={saveInfo} className="bg-green-500 text-green-950 px-6 py-2 rounded-lg text-sm font-black shadow-lg hover:bg-green-400 transform active:scale-95 transition-all">GUARDAR</button></div>
                        </div>
                    ) : (
                        <div className="relative flex flex-col gap-2 pt-2 text-center">
                             <div className="w-full overflow-x-auto no-scrollbar pb-2"><p className="font-mono text-xl sm:text-3xl font-normal text-white/95 whitespace-nowrap drop-shadow-md" style={{fontFamily: "'Share Tech Mono', monospace", letterSpacing: "0.05em"}}>{formatCardNumber(displayInfo.number)}</p></div>
                             <div className="flex justify-center items-center gap-4 border-t border-white/10 pt-4"><button onClick={copyInfo} className="text-white/70 hover:text-white transition-colors flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest bg-white/10 px-6 py-2 rounded-full backdrop-blur-sm border border-white/5 hover:bg-white/20 shadow-lg"><Copy size={14}/> COPIAR NÚMERO</button></div>
                             {displayInfo.details && <div className="w-full text-left pl-2 border-l-2 border-green-400/50 mt-2"><p className="text-sm text-gray-300 font-light whitespace-pre-wrap leading-relaxed">{displayInfo.details}</p></div>}
                        </div>
                    )}
                </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200"><tr><th className="p-5 text-left text-gray-400 font-bold uppercase text-xs tracking-wider sticky left-0 bg-gray-50 w-48 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] z-10">Participante</th>{fullJornadas.map(j => <th key={j} className="p-5 text-center text-gray-400 font-bold uppercase text-xs whitespace-nowrap min-w-[80px]">{shortRoundName(j)}</th>)}</tr></thead>
                        <tbody className="divide-y divide-gray-100">
                            {participants.map(p => (
                                <tr key={p.uid} className="hover:bg-blue-50/30 transition-colors">
                                    <td className="p-5 font-bold text-gray-700 flex items-center gap-3 sticky left-0 bg-white border-r border-gray-100 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] z-10"><UserAvatar url={p.photoURL} name={p.name} /><span className="font-light tracking-tight">{p.name}</span></td>
                                    {fullJornadas.map(j => {
                                        const status = payments[p.uid]?.[j.replace(/\s/g, '_')];
                                        return (
                                            <td key={j} className="p-5 text-center"><button onClick={() => toggle(p.uid, j)} disabled={!isAdmin && (p.uid !== user.uid || status === 'admin_confirmed')} className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all shadow-sm transform active:scale-90 mx-auto ${status === 'admin_confirmed' ? 'bg-green-500 text-white shadow-green-200 ring-2 ring-green-100' : (status === 'user_paid' ? 'bg-blue-500 text-white shadow-blue-200 ring-2 ring-blue-100' : 'bg-gray-100 text-gray-300 border border-gray-200 hover:border-gray-300')}`} title={status === 'admin_confirmed' ? 'Pago Confirmado' : (status === 'user_paid' ? 'Pago Enviado' : 'Pendiente')}>{status === 'admin_confirmed' ? <Shield size={18}/> : (status === 'user_paid' ? <CheckIcon size={18}/> : <DollarSign size={16}/>)}</button></td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <AppFooter />
        </div>
    );
};

function ArenaView({ group, isAdmin }) {
    const [wsLink, setWsLink] = useState(group.whatsappLink || '');
    const [isEditing, setIsEditing] = useState(false);

    const handleSaveLink = async () => {
        await updateDoc(doc(db, 'groups', group.id), { whatsappLink: wsLink });
        setIsEditing(false);
    };

    return (
        <div className="flex flex-col h-full bg-arena relative overflow-hidden items-center justify-center p-6 text-center text-white">
            <div className="bg-slate-900/80 backdrop-blur-md p-8 rounded-3xl border border-white/10 shadow-2xl max-w-md w-full">
                <WhatsAppIcon className="w-20 h-20 mx-auto text-[#25D366] mb-6 drop-shadow-[0_0_15px_rgba(37,211,102,0.5)]" />
                <h2 className="text-3xl font-black mb-2 tracking-tight font-wc">Futsie Arena</h2>
                <p className="text-sm text-gray-300 mb-8 font-light leading-relaxed">
                    Toda la pasión y carrilla de los partidos ahora se vive directo en el grupo oficial de WhatsApp de esta liga.
                </p>

                {group.whatsappLink ? (
                    <div className="space-y-4">
                        <a 
                            href={group.whatsappLink} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-4 px-6 rounded-2xl font-black text-lg transition-all shadow-[0_0_20px_rgba(37,211,102,0.3)] hover:shadow-[0_0_30px_rgba(37,211,102,0.5)] flex items-center justify-center gap-3 transform hover:scale-105 active:scale-95"
                        >
                            <MessageSquare size={24} /> Entrar al Chat
                        </a>
                        {isAdmin && (
                            <button onClick={() => setIsEditing(!isEditing)} className="text-xs text-gray-400 hover:text-white uppercase tracking-widest font-bold underline underline-offset-4">
                                Editar Enlace
                            </button>
                        )}
                    </div>
                ) : (
                    isAdmin ? (
                        <div className="space-y-4">
                            <p className="text-yellow-400 text-xs font-bold uppercase tracking-widest">Aún no hay enlace configurado</p>
                            {!isEditing ? (
                                <button onClick={() => setIsEditing(true)} className="w-full bg-white/10 hover:bg-white/20 text-white py-3 px-6 rounded-xl font-bold transition-all border border-white/20 flex items-center justify-center gap-2">
                                    <LinkIcon size={18} /> Agregar Link de WhatsApp
                                </button>
                            ) : null}
                        </div>
                    ) : (
                        <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                            <p className="text-gray-400 text-sm font-medium">El administrador aún no ha configurado el enlace al grupo de WhatsApp.</p>
                        </div>
                    )
                )}

                {isEditing && isAdmin && (
                    <div className="mt-6 space-y-3 animate-fade-in bg-black/40 p-4 rounded-2xl border border-white/10">
                        <label className="text-[10px] text-gray-400 uppercase tracking-widest font-bold text-left block">Enlace de Invitación de WhatsApp</label>
                        <input 
                            type="url" 
                            value={wsLink} 
                            onChange={(e) => setWsLink(e.target.value)} 
                            placeholder="https://chat.whatsapp.com/..." 
                            className="w-full bg-transparent border-b-2 border-white/20 py-2 text-white placeholder-white/30 outline-none focus:border-[#25D366] transition-colors text-sm"
                        />
                        <div className="flex gap-2 pt-2">
                            <button onClick={() => setIsEditing(false)} className="flex-1 py-2 text-xs font-bold text-gray-400 hover:text-white">Cancelar</button>
                            <button onClick={handleSaveLink} className="flex-1 bg-[#25D366] text-white py-2 rounded-xl text-xs font-black shadow-md hover:bg-[#128C7E]">Guardar Link</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const GroupsDashboard = ({ user, onSelectGroup, onCreateGroup, onJoinGroup, onDeleteGroup, onPurchase }) => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [comp, setComp] = useState('WORLD_CUP_26');
    const [deletingId, setDeletingId] = useState(null);
    const [showStore, setShowStore] = useState(false);
    const [profile, setProfile] = useState(null);
    const [showLogout, setShowLogout] = useState(false);
    const [showAliasModal, setShowAliasModal] = useState(false);
    const [showManual, setShowManual] = useState(false);

    useEffect(() => {
        if (!user) return;
        const unsub = onSnapshot(collection(db, `users/${user.uid}/groupsMemberOf`), async (snap) => {
            const gids = snap.docs.map(d => d.id);
            try {
                // Pedimos cada liga por separado, en paralelo.
                // Antes se usaba una sola consulta con documentId() 'in', que:
                //   · tenía tope de 10 ligas (la 11 en adelante no se veían)
                //   · si UNA liga fallaba, tumbaba la pantalla completa
                const resultados = await Promise.all(gids.map(async (gid) => {
                    try {
                        const s = await getDoc(doc(db, 'groups', gid));
                        return s.exists()
                            ? { id: s.id, datos: { id: s.id, ...s.data() } }
                            : { id: gid, borrada: true };
                    } catch (e) {
                        // Sin permiso: puede ser temporal. No la mostramos,
                        // pero tampoco borramos la referencia por si acaso.
                        console.warn(`No se pudo leer la liga ${gid}:`, e.code || e.message);
                        return { id: gid, inaccesible: true };
                    }
                }));

                setGroups(resultados.filter(r => r.datos).map(r => r.datos));

                // Autolimpieza: si la liga YA NO EXISTE, quitamos la referencia
                // muerta del perfil. Solo cuando estamos seguros de que se borró.
                resultados
                    .filter(r => r.borrada)
                    .forEach(r => {
                        deleteDoc(doc(db, `users/${user.uid}/groupsMemberOf/${r.id}`))
                            .catch(() => {});
                    });
            } catch (e) {
                console.error("Error cargando ligas:", e);
                setGroups([]);
            } finally {
                // Pase lo que pase, la pantalla deja de decir "Cargando..."
                setLoading(false);
            }
        }, (error) => {
            console.error("Error escuchando membresías:", error);
            setGroups([]);
            setLoading(false);
        });
        const unsubProfile = onSnapshot(doc(db, 'users', user.uid), (doc) => setProfile(doc.data()));
        return () => { unsub(); unsubProfile(); };
    }, [user]);

    const handleDelete = async (e, group) => { e.stopPropagation(); await onDeleteGroup(group); setDeletingId(null); };
    const handleRefresh = () => { localStorage.clear(); sessionStorage.clear(); window.location.reload(true); };
    const copyToClipboard = (text) => {
        try { navigator.clipboard.writeText(text); } catch (e) { console.error("No se pudo copiar", e); }
    };

    const handleUpdateAlias = async (newAlias) => {
        if (!newAlias.trim()) return;
        await updateDoc(doc(db, 'users', user.uid), { alias: newAlias });
        const updates = groups.map(g => updateDoc(doc(db, `groups/${g.id}/participants/${user.uid}`), { name: newAlias }));
        await Promise.all(updates);
        setShowAliasModal(false);
    };

    const handleCreateGroup = () => {
        if (COMPETITIONS[comp].status === 'inactive') {
            alert("Esta competición no está disponible en este momento.");
            return;
        }
        if(name) { onCreateGroup(name, comp); setName(''); }
    };

    const displayName = profile?.alias || user.displayName;

    return (
        <div className="w-full h-full overflow-y-auto no-scrollbar">
            <div className="p-6 pb-32 max-w-7xl mx-auto">
                 <AliasModal isOpen={showAliasModal} onClose={() => setShowAliasModal(false)} onSave={handleUpdateAlias} currentAlias={displayName} />
                 {showStore && <div className="fixed inset-0 z-[100] bg-gray-50 flex flex-col animate-fade-in overflow-y-auto"><div className="p-4 flex justify-between items-center bg-white shadow-sm sticky top-0 z-10"><h2 className="font-black text-xl">Tienda Futsie</h2><button onClick={() => setShowStore(false)} className="bg-gray-100 p-2 rounded-full"><X size={20}/></button></div><div className="flex-1"><StoreView userProfile={profile} onPurchase={onPurchase} /></div></div>}
                 {showManual && <FutsieManual onClose={() => setShowManual(false)} />}
                 {showLogout && <div className="fixed inset-0 z-40" onClick={() => setShowLogout(false)}></div>}

                <div className="flex flex-col md:flex-row justify-between items-center mb-10 gap-4 relative z-50">
                     <div className="flex items-center gap-4 w-full md:w-auto" onClick={() => setShowManual(true)} style={{cursor: 'pointer'}}>
                        <div className="transform hover:scale-105 transition-transform active:scale-95"><FutsieLogo scale={1.2} light={false} /></div>
                     </div>

                     <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                        <button onClick={handleRefresh} className="text-xs bg-white border border-gray-200 px-4 py-2 rounded-full font-bold text-gray-500 hover:bg-gray-50 flex items-center gap-2 transition-colors shadow-sm"><RefreshIcon size={14}/> Refrescar</button>
                        <button onClick={() => setShowStore(true)} className="text-xs bg-white border border-gray-200 px-4 py-2 rounded-full font-bold text-gray-500 hover:bg-gray-50 flex items-center gap-2 transition-colors shadow-sm"><ShoppingBag size={14}/> Tienda</button>
                        <div className="relative">
                            <div onClick={() => setShowLogout(!showLogout)} className="flex items-center gap-3 bg-white p-1 pr-4 rounded-full border border-gray-200 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors">
                                 <UserAvatar url={user.photoURL} name={displayName} size="w-8 h-8" />
                                 <span className="text-sm font-bold text-gray-700 truncate max-w-[100px]">{displayName}</span>
                            </div>
                            {showLogout && (
                                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-slate-100 py-2 w-48 z-50 animate-fade-in">
                                    <button onClick={() => { setShowAliasModal(true); setShowLogout(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 font-bold hover:bg-gray-50 flex items-center gap-2 border-b border-slate-100"><Edit3 size={16}/> Cambiar Alias</button>
                                    <button onClick={() => signOut(auth)} className="w-full text-left px-4 py-2 text-sm text-red-600 font-bold hover:bg-red-50 flex items-center gap-2"><LogOut size={16}/> Cerrar Sesión</button>
                                </div>
                            )}
                        </div>
                     </div>
                </div>

                <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-6 border-b border-gray-200 pb-2">Mis grupos</h2>
                {loading ? <div className="text-gray-400 text-center py-20 animate-pulse">Cargando tus ligas...</div> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
                        {groups.length === 0 && <div className="col-span-full text-center py-10 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200"><p className="text-gray-400 font-medium">No estás en ningún grupo aún.</p></div>}
                        {groups.map(g => (
                            <div key={g.id} onClick={() => onSelectGroup(g)} className="bg-white p-5 rounded-3xl shadow-sm border border-gray-100 flex flex-col justify-between cursor-pointer hover:shadow-lg hover:border-blue-100 transition-all group h-48 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-50 rounded-bl-full -mr-6 -mt-6 z-0 transition-colors group-hover:bg-blue-50"></div>
                                <div className="relative z-10 flex justify-between items-start">
                                    <div className="w-14 h-14 flex items-center justify-center bg-white rounded-2xl shadow-sm border border-gray-100 p-2"><CompetitionLogo competitionKey={g.competitionKey} className="max-w-full max-h-full p-1" /></div>
                                    <div className="flex gap-2">
                                        {user.uid === g.adminUid && (<button onClick={(e) => { e.stopPropagation(); setDeletingId(deletingId === g.id ? null : g.id); }} className="text-gray-300 hover:text-red-500 transition-colors p-2"><Trash2 size={18} /></button>)}
                                    </div>
                                </div>
                                {deletingId === g.id ? (
                                    <div className="absolute inset-0 bg-white/95 backdrop-blur flex items-center justify-center z-20 flex-col gap-3 p-6 text-center rounded-3xl">
                                        <p className="text-sm font-bold text-red-600">¿Eliminar este grupo permanentemente?</p>
                                        <div className="flex gap-2 w-full"><button onClick={(e) => {e.stopPropagation(); setDeletingId(null)}} className="flex-1 bg-gray-100 text-gray-600 text-xs py-2 rounded-xl font-bold hover:bg-gray-200">Cancelar</button><button onClick={(e) => handleDelete(e, g)} className="flex-1 bg-red-500 text-white text-xs py-2 rounded-xl font-bold shadow-md hover:bg-red-600">Sí, Borrar</button></div>
                                    </div>
                                ) : (
                                    <div className="relative z-10 mt-auto">
                                        <h3 className="font-bold text-gray-800 text-xl leading-tight truncate">{g.groupName}</h3>
                                        <div className="flex items-center justify-between mt-3">
                                            <div className="flex items-center gap-2"><span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200">Código: <span className="text-gray-900 font-mono">{g.shareCode}</span></span><button onClick={(e) => { e.stopPropagation(); copyToClipboard(g.shareCode); }} className="bg-gray-100 hover:bg-gray-200 p-1.5 rounded-lg text-gray-500 transition-colors" title="Copiar código"><Copy size={14} /></button></div>
                                            <span className="text-blue-500 font-bold text-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">Entrar <ArrowLeft size={12} className="rotate-180"/></span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                <div className="grid md:grid-cols-2 gap-8">
                     <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100 relative overflow-hidden">
                        <div className="relative z-10">
                            <h2 className="text-xl font-black text-gray-800 mb-6 flex items-center gap-2"><TrophyIcon className="text-yellow-500" size={24}/> Crear Nueva Liga</h2>
                            <label className="block text-xs font-bold text-gray-400 mb-3 uppercase tracking-wider">1. Elige Competición</label>
                            <div className="grid grid-cols-3 gap-3 mb-6">
                                {Object.entries(COMPETITIONS).map(([k, c]) => (
                                    <button 
                                        key={k} 
                                        onClick={() => setComp(k)} 
                                        className={`p-3 rounded-2xl border-2 flex items-center justify-center gap-2 transition-all ${
                                            c.isWorldCup 
                                                ? (comp === k ? 'border-yellow-400 bg-gradient-to-r from-purple-800 to-indigo-800 shadow-[0_0_15px_rgba(250,204,21,0.5)] transform scale-[1.02] z-10' : 'border-purple-500/50 bg-gradient-to-r from-purple-900 to-indigo-900 opacity-90 hover:opacity-100')
                                                : (comp === k ? 'border-blue-500 bg-blue-50/50 transform scale-[1.02] z-10' : 'border-gray-100 bg-white hover:border-gray-200')
                                        } ${c.isWorldCup ? 'col-span-3 flex-row h-20' : 'flex-col h-28'}`}
                                    >
                                        <div className={`${c.isWorldCup ? 'w-12 h-12' : 'w-14 h-14'} flex items-center justify-center relative`}>
                                            <CompetitionLogo competitionKey={k} className={`max-w-full max-h-full ${c.status === 'inactive' ? 'opacity-50 grayscale' : ''}`} />
                                            {c.status === 'inactive' && <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-full"><CalendarX size={20} className="text-gray-500"/></div>}
                                        </div>
                                        <span className={`font-bold text-center leading-none ${c.isWorldCup ? 'text-sm text-white font-wc uppercase tracking-widest ml-2' : 'text-[9px] text-gray-600'}`}>{c.name}</span>
                                    </button>
                                ))}
                            </div>
                            <label className="block text-xs font-bold text-gray-400 mb-3 uppercase tracking-wider">2. Nombre del Grupo</label>
                            <div className="flex flex-col gap-3">
                                <input value={name} onChange={e => setName(e.target.value)} placeholder="Ej. Los Campeones" className="flex-1 bg-gray-50 border-2 border-gray-100 p-4 rounded-2xl text-sm font-bold text-gray-700 outline-none focus:border-blue-500 transition-colors w-full" />
                                <button onClick={handleCreateGroup} className="bg-blue-600 text-white px-8 py-3 rounded-2xl font-bold text-sm hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all w-full">Crear</button>
                            </div>
                        </div>
                    </div>
                     <div className="bg-slate-900 p-8 rounded-3xl shadow-xl text-white relative overflow-hidden flex flex-col justify-center">
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                        <div className="relative z-10">
                            <h2 className="text-xl font-black mb-2 flex items-center gap-3"><Users className="text-blue-400" size={24}/> Unirse a una Liga</h2>
                            <p className="text-slate-400 text-sm mb-8 font-medium">Ingresa el código de 6 dígitos que te compartieron tus amigos para entrar a la competencia.</p>
                            <div className="flex flex-col gap-3">
                                <input value={code} onChange={e => setCode(e.target.value)} placeholder="CÓDIGO" className="flex-1 bg-white/10 border-2 border-white/10 p-4 rounded-2xl text-sm font-bold text-white outline-none focus:border-blue-400 transition-colors uppercase tracking-widest placeholder-white/30 text-center w-full" />
                                <button onClick={() => { if(code) { onJoinGroup(code); setCode(''); } }} className="bg-white text-slate-900 px-8 py-3 rounded-2xl font-bold text-sm hover:bg-gray-100 shadow-lg transition-all w-full">Unirse</button>
                            </div>
                        </div>
                    </div>
                </div>
                <AppFooter />
            </div>
        </div>
    );
};

const useJornadaMonitor = (fixtures, selectedJornada, userUid, hasPredictions, hasPayment) => {
    useEffect(() => {
        if (!fixtures || !selectedJornada || !fixtures[selectedJornada]) return;
        
        const checkSchedule = () => {
            const now = new Date();
            const matches = [...fixtures[selectedJornada]].sort((a,b) => new Date(a.fixture.date) - new Date(b.fixture.date));
            const firstMatch = matches[0];
            if (!firstMatch) return;

            const matchDate = new Date(firstMatch.fixture.date);
            const diffMs = matchDate - now;
            const diffHours = diffMs / (1000 * 60 * 60);

            const windows = [24, 6, 3, 1];
            const tolerance = 0.25;

            if (!hasPayment && Notification.permission === 'granted') {
                for (let w of windows) {
                    if (Math.abs(diffHours - w) < tolerance) {
                        const id = `alert_pay_${selectedJornada}_${w}h`;
                        if (!sessionStorage.getItem(id)) {
                            try {
                                new Notification("Futsie Alerta ⚡️", {
                                    body: `¡Faltan ${w} horas para la ${formatRoundName(selectedJornada)}! Recuerda pagar tu cuota de $100 MXN para participar por la bolsa.`,
                                    icon: '/favicon.ico'
                                });
                                sessionStorage.setItem(id, 'true');
                            } catch (e) { console.log("Notif error", e); }
                        }
                    }
                }
            }
        };

        const timer = setInterval(checkSchedule, 60000);
        checkSchedule();
        return () => clearInterval(timer);
    }, [fixtures, selectedJornada, hasPredictions, hasPayment]);
};

const QuinielaApp = ({ user, group, onExit }) => {
    const [view, setView] = useState('pronosticos');
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ fixtures: {}, participants: [], predictions: {}, payments: {}, profile: {}, broadcasterData: {} });
    const [selectedJornada, setSelectedJornada] = useState(null);
    const [fixtureOverrides, setFixtureOverrides] = useState({});
    const [showStore, setShowStore] = useState(false);
    const [showManual, setShowManual] = useState(false);
    const scrollContainerRef = useRef(null);
    const [dismissedNotifs, setDismissedNotifs] = useState([]);
    
    const notificationsShownRef = useRef(false);

    const compKey = group.competitionKey || 'LIGA_MX';
    const baseComp = COMPETITIONS[compKey];

    // --- MAGIA DE RETROCOMPATIBILIDAD ---
    // Si el grupo tiene season guardada (nuevo), usa '2026' (Apertura). Si es viejo, usa '2025' (Clausura).
    const groupSeason = group.season || (compKey === 'LIGA_MX' ? '2025' : baseComp.season);
    const groupRoundFilter = group.roundFilter !== undefined ? group.roundFilter : (compKey === 'LIGA_MX' ? 'Clausura' : null);
    // ------------------------------------

    const isLeaguesCup = compKey === 'LEAGUES_CUP';
    const isWorldCup = compKey === 'WORLD_CUP_26';
    const isAdmin = user.uid === group.adminUid;

    const roundKeys = useMemo(() => {
         if (!data.fixtures) return [];
         return Object.keys(data.fixtures).sort((a,b) => parseInt(a.replace(/\D/g,'')) - parseInt(b.replace(/\D/g,'')));
    }, [data.fixtures]);

    const pendingFixtures = useMemo(() => {
        if (!data.participants || !selectedJornada || !data.fixtures[selectedJornada]) return [];
        return data.fixtures[selectedJornada].filter(f => new Date(f.fixture.date) > new Date() && !data.predictions[`${user.uid}_${f.fixture.id}`]);
    }, [data.participants, data.fixtures, data.predictions, selectedJornada, user.uid]);

    const paymentStatus = useMemo(() => {
        if (!selectedJornada || !user || !data.payments) return null;
        const key = selectedJornada.replace(/\s/g, '_');
        return data.payments[user.uid]?.[key];
    }, [selectedJornada, user, data.payments]);

    const hasPaid = paymentStatus === 'admin_confirmed' || paymentStatus === 'user_paid';
    const hasPredictedAll = pendingFixtures.length === 0;

    useJornadaMonitor(data.fixtures, selectedJornada, user?.uid, hasPredictedAll, hasPaid);

    const handleDismiss = useCallback((id) => {
        setDismissedNotifs(prev => [...prev, id]);
    }, []);

    const notifications = useMemo(() => {
        if (notificationsShownRef.current) return [];

        const list = [];
        if(selectedJornada && user && data.payments) {
             if(!hasPaid) {
                 list.push({
                    id: `pay_${selectedJornada}`,
                    type: 'payment',
                    title: 'Pago Pendiente',
                    text: `Recuerda realizar tu pago ($100 MXN) para la ${formatRoundName(selectedJornada)} y marcarlo como enviado para concursar por la bolsa.`
                });
             }
        }

        if(selectedJornada && data.fixtures && data.fixtures[selectedJornada]) {
            const allFinished = data.fixtures[selectedJornada].every(f => ['FT', 'AET', 'PEN'].includes(f.fixture.status.short));
            if(allFinished) {
                 list.push({
                    id: `res_${selectedJornada}`,
                    type: 'results',
                    title: 'Resultados Listos',
                    text: `La ${formatRoundName(selectedJornada)} ha finalizado. Revisa la tabla de posiciones para ver quién se llevó la bolsa.`
                });
            }
        }
        
        return list.filter(n => !dismissedNotifs.includes(n.id));
    }, [pendingFixtures, hasPaid, data.fixtures, selectedJornada, user, dismissedNotifs, data.payments]);

    useEffect(() => {
        if (notifications.length > 0 && !notificationsShownRef.current) {
            notificationsShownRef.current = true; 
        }
    }, [notifications.length]);

    const loadFixtures = useCallback(async () => {
        setLoading(true);
        try {
            const raw = await apiClient(`/fixtures?league=${baseComp.id}&season=${groupSeason}`);
            
            if (raw) {
                let filteredRaw = raw;
                if (groupRoundFilter) {
                    const filtrados = raw.filter(f => f.league.round.toLowerCase().includes(groupRoundFilter.toLowerCase()));
                    if (filtrados.length > 0) filteredRaw = filtrados;
                }
                const grouped = filteredRaw.reduce((acc, f) => { const r = f.league.round; if (!acc[r]) acc[r] = []; acc[r].push(f); return acc; }, {});
                const sortedKeys = Object.keys(grouped).sort((a,b) => parseInt(a.replace(/\D/g,'')) - parseInt(b.replace(/\D/g,'')));
                let active = sortedKeys[0];
                for (let r of sortedKeys) { if (grouped[r].some(g => g.fixture.status.short === 'NS' || g.fixture.status.short === '1H')) { active = r; break; } }
                Object.values(grouped).forEach(list => list.sort((a,b) => new Date(a.fixture.date) - new Date(b.fixture.date)));
                setData(prev => ({ ...prev, fixtures: grouped }));
                setSelectedJornada(active);
            }
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [baseComp, groupSeason, groupRoundFilter]);

    useEffect(() => {
        loadFixtures();

        const u1 = onSnapshot(doc(db, 'users', user.uid), s => setData(p => ({ ...p, profile: s.data() })));
        const u2 = onSnapshot(collection(db, `groups/${group.id}/participants`), s => setData(p => ({ ...p, participants: s.docs.map(d=>({uid:d.id, ...d.data()})) })));
        const u3 = onSnapshot(collection(db, `groups/${group.id}/predictions`), s => { const x={}; s.docs.forEach(d=>x[d.id]=d.data()); setData(p => ({ ...p, predictions: x })); });
        const u4 = onSnapshot(collection(db, `groups/${group.id}/payments`), s => { const x={}; s.docs.forEach(d=>x[d.id]=d.data()); setData(p => ({ ...p, payments: x })); });
        const u5 = onSnapshot(collection(db, `groups/${group.id}/fixtureOverrides`), s => { const overrides = {}; s.docs.forEach(doc => overrides[doc.id] = doc.data()); setFixtureOverrides(overrides); });
        
        const fetchBroadcasters = async () => {
            try {
                const url = "https://firestore.googleapis.com/v1/projects/futdate-app/databases/(default)/documents/artifacts/futdate-admin-26/public/data/agenda/master";
                const response = await fetch(url);
                if (response.ok) {
                    const dataResp = await response.json();
                    if (dataResp && dataResp.fields) {
                        const formattedData = {};
                        for (const regionKey in dataResp.fields) {
                            formattedData[regionKey] = {};
                            const regionObj = dataResp.fields[regionKey].mapValue?.fields;
                            if (regionObj) {
                                for (const matchId in regionObj) {
                                    formattedData[regionKey][matchId] = regionObj[matchId].stringValue || "";
                                }
                            }
                        }
                        setData(p => ({ ...p, broadcasterData: formattedData }));
                    }
                }
            } catch (error) {
                console.error("Error fetching broadcasters:", error);
            }
        };
        fetchBroadcasters();

        return () => { u1(); u2(); u3(); u4(); u5(); };
    }, [group.id, user.uid, loadFixtures]);

    useEffect(() => {
        if (view === 'resultados' && data.fixtures && Object.keys(data.fixtures).length > 0) {
            const sorted = Object.keys(data.fixtures).sort((a,b) => parseInt(a.replace(/\D/g,'')) - parseInt(b.replace(/\D/g,'')));
            let lastFinished = null;
            for (let r of sorted) { if (data.fixtures[r].some(f => ['FT', 'AET', 'PEN'].includes(f.fixture.status.short))) { lastFinished = r; } }
            if (lastFinished) setSelectedJornada(lastFinished);
        }
    }, [view, data.fixtures]);

    const handleSavePrediction = async (d) => {
        const pid = `${user.uid}_${d.fixtureId}`;
        await setDoc(doc(db, `groups/${group.id}/predictions`, pid), { 
            ...d, 
            uid: user.uid, 
            participantName: user.displayName,
            submittedAt: new Date().toISOString()
        }, { merge: true });
    };

    const handlePurchase = async (item) => {
        const ref = doc(db, 'users', user.uid);
        if (item.type === 'coins') {
            // Ver nota en buy(): sumar monedas es trabajo del servidor, no del navegador.
            alert("La compra de monedas todavía no está disponible.");
            return;
        }
        if ((data.profile?.inventory?.coins || 0) < item.price) return alert("Saldo insuficiente");
        await updateDoc(ref, { 'inventory.coins': increment(-item.price), [`inventory.${item.id}`]: increment(1) });
    };

    const handleUsePowerUp = async (pid) => {
        if ((data.profile?.inventory?.[pid] || 0) <= 0) return false;
        await updateDoc(doc(db, 'users', user.uid), { [`inventory.${pid}`]: increment(-1) });
        return true;
    };

    const handleToggleLock = async (fixtureId, isPast) => {
        if (!isAdmin) return;
        const currentOverride = fixtureOverrides[fixtureId];
        let newState = null;
        if (!currentOverride) {
             if (isPast) newState = { unlocked: true };
             else newState = { locked: true };
        }
        else if (currentOverride.locked) { newState = { unlocked: true }; }
        else { newState = null; }

        const ref = doc(db, `groups/${group.id}/fixtureOverrides`, fixtureId.toString());
        if (newState) await setDoc(ref, newState); else await deleteDoc(ref);
    };

    const handlePaymentUpdate = async (uid, j, status) => {
        const key = j.replace(/\s/g, '_');
        await setDoc(doc(db, `groups/${group.id}/payments`, uid), { [key]: status }, { merge: true });
    };

    const handleScrollToTop = () => { if(scrollContainerRef.current) scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' }); };

    if (loading) return <div className="flex h-screen items-center justify-center text-gray-400">Cargando datos...</div>;

    return (
        <div className="flex flex-col h-full bg-transparent">
             {showStore && <div className="fixed inset-0 z-[100] bg-gray-50 flex flex-col animate-fade-in overflow-y-auto"><div className="p-4 flex justify-between items-center bg-white shadow-sm sticky top-0 z-10"><h2 className="font-black text-xl">Tienda Futsie</h2><button onClick={() => setShowStore(false)} className="bg-gray-100 p-2 rounded-full"><X size={20}/></button></div><div className="flex-1"><StoreView userProfile={data.profile} onPurchase={handlePurchase} /></div></div>}
             {showManual && <FutsieManual onClose={() => setShowManual(false)} />}
             <NotificationSystem notifications={notifications} onDismiss={handleDismiss} />

            <div className="flex-none">
                <QuinielaHeader groupName={group.groupName} competitionName={baseComp.name} competitionKey={compKey} onExitGroup={onExit} />
                <QuinielaNav activeView={view} setActiveView={setView} />
                {(view === 'pronosticos' || view === 'resultados') && (
                    <JornadaNav jornadas={roundKeys} selectedJornada={selectedJornada} onSelectJornada={setSelectedJornada} />
                )}
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar" ref={scrollContainerRef}>
                {view === 'arena' && <ArenaView group={group} user={user} isAdmin={isAdmin} />}
                {view === 'pronosticos' && (
                    <>
                        <div className="max-w-7xl mx-auto p-4 space-y-4 pb-48 pt-4 relative">
                            {data.fixtures[selectedJornada]?.map(f => (
                                <FixtureCard
                                    key={f.fixture.id}
                                    fixture={f}
                                    prediction={data.predictions[`${user.uid}_${f.fixture.id}`]}
                                    onSave={handleSavePrediction}
                                    isLeaguesCup={isLeaguesCup}
                                    isWorldCup={isWorldCup}
                                    userProfile={data.profile}
                                    onUsePowerUp={handleUsePowerUp}
                                    competitionKey={compKey}
                                    isAdmin={isAdmin}
                                    onToggleLock={handleToggleLock}
                                    isManuallyLocked={fixtureOverrides[f.fixture.id]?.locked}
                                    isManuallyUnlocked={fixtureOverrides[f.fixture.id]?.unlocked}
                                    liveBroadcaster={data.broadcasterData?.MX?.[f.fixture.id]}
                                />
                            ))}
                        </div>
                        <AppFooter />
                    </>
                )}
                {view === 'resultados' && <ResultsView fixtures={data.fixtures} predictions={data.predictions} participants={data.participants} selectedJornada={selectedJornada} isWorldCup={isWorldCup} />}
                {view === 'tabla' && <LeaderboardView fixtures={Object.values(data.fixtures).flat()} predictions={data.predictions} participants={data.participants} payments={data.payments} selectedJornada={selectedJornada} isWorldCup={isWorldCup} />}
                {view === 'pagos' && <ControlPagosView participants={data.participants} jornadas={roundKeys} payments={data.payments} onUpdate={handlePaymentUpdate} user={user} isAdmin={isAdmin} group={group} />}
                {view === 'tienda' && <div className="p-4 pt-8"><StoreView userProfile={data.profile} onPurchase={handlePurchase} /><AppFooter/></div>}
                {view === 'participantes' && (
                    <div className="p-6 pb-24 grid gap-4 max-w-4xl mx-auto pt-8">
                        {data.participants.map(p => (
                            <MemberCard key={p.uid} member={p} predictions={data.predictions} fixtures={data.fixtures} isWorldCup={isWorldCup} />
                        ))}
                        <AppFooter/>
                    </div>
                )}
            </div>

            {view !== 'arena' && <ScrollToTopFAB onClick={handleScrollToTop} />}
        </div>
    );
};

export default function App() {
    const [user, setUser] = useState(null);
    const [activeGroup, setActiveGroup] = useState(null);
    const [modal, setModal] = useState(false);
    const [showUpdateNotice, setShowUpdateNotice] = useState(false);

    useEffect(() => {
        document.title = "Futsie";
        const unsub = onAuthStateChanged(auth, setUser);
        
        if (!localStorage.getItem('update_transparency_v2')) {
            setShowUpdateNotice(true);
        }

        return () => unsub();
    }, []);

    const handleCloseUpdateNotice = () => {
        localStorage.setItem('update_transparency_v2', 'true');
        setShowUpdateNotice(false);
    };

    const login = async () => {
        const res = await signInWithPopup(auth, provider);
        const ref = doc(db, 'users', res.user.uid);
        if (!(await getDoc(ref)).exists()) await setDoc(ref, { displayName: res.user.displayName, email: res.user.email, inventory: { coins: 100 }, photoURL: res.user.photoURL });
        else await updateDoc(ref, { photoURL: res.user.photoURL });
    };

    const createGroup = async (name, key) => {
        if (!user) return;
        if (COMPETITIONS[key].type === 'premium') { setModal(true); return; }
        const code = Math.random().toString(36).substring(2, 8).toUpperCase();
        const ref = doc(collection(db, 'groups'));
        const batch = writeBatch(db);
        
        batch.set(ref, { 
            groupName: name, 
            competitionKey: key, 
            season: COMPETITIONS[key].season || '2026', 
            roundFilter: COMPETITIONS[key].roundFilter || null, 
            shareCode: code, 
            adminUid: user.uid, 
            createdAt: serverTimestamp() 
        });

        batch.set(doc(db, `users/${user.uid}/groupsMemberOf/${ref.id}`), { joined: serverTimestamp() });
        batch.set(doc(db, `groups/${ref.id}/participants/${user.uid}`), { name: user.displayName, uid: user.uid, photoURL: user.photoURL });
        // Índice público código → liga. Permite unirse sin tener que exponer
        // la lista completa de ligas (que trae los datos bancarios del admin).
        batch.set(doc(db, 'groupCodes', code), { groupId: ref.id });
        await batch.commit();
    };

    const joinGroup = async (code) => {
        const clave = code.toUpperCase().trim();
        const indice = await getDoc(doc(db, 'groupCodes', clave));
        if (!indice.exists()) return alert("Código no encontrado");
        const gid = indice.data().groupId;
        const batch = writeBatch(db);
        batch.set(doc(db, `users/${user.uid}/groupsMemberOf/${gid}`), { joined: serverTimestamp() });
        batch.set(doc(db, `groups/${gid}/participants/${user.uid}`), { name: user.displayName, uid: user.uid, photoURL: user.photoURL });
        await batch.commit();
    };

    const deleteGroup = async (g) => {
        await deleteDoc(doc(db, 'groups', g.id));
        await deleteDoc(doc(db, `users/${user.uid}/groupsMemberOf/${g.id}`));
    };

    const buy = async (item) => {
        if (!user) return;
        const ref = doc(db, 'users', user.uid);
        if (item.type === 'coins') {
            // Las monedas ya NO se pueden sumar desde el navegador: las reglas
            // de Firestore lo impiden (antes cualquiera se regalaba las que
            // quisiera). Cuando Stripe esté conectado, quien las acredita es
            // el webhook del servidor.
            alert("La compra de monedas todavía no está disponible.");
            return;
        }
        const snap = await getDoc(ref);
        const coins = snap.data()?.inventory?.coins || 0;
        if (coins < item.price) return alert("Saldo insuficiente");
        await updateDoc(ref, { 'inventory.coins': increment(-item.price), [`inventory.${item.id}`]: increment(1) });
    };

    const bgClass = activeGroup && activeGroup.competitionKey !== 'WORLD_CUP_26'
        ? (activeGroup.competitionKey === 'LEAGUES_CUP' ? 'bg-gradient-to-br from-indigo-900 to-purple-900'
          : activeGroup.competitionKey === 'LA_LIGA' ? 'bg-gradient-to-br from-red-700 to-orange-900'
          : 'bg-gradient-to-br from-blue-900 to-slate-900')
        : 'bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-50 via-gray-100 to-slate-200';

    return (
        <div className={`h-[100dvh] fixed inset-0 font-sans text-gray-900 transition-colors duration-500 flex flex-col overflow-hidden`}>
            <GlobalStylesLoader />
            
            {activeGroup?.competitionKey === 'WORLD_CUP_26' ? (
                <WorldCupBackground />
            ) : (
                <div className={`fixed inset-0 z-0 ${bgClass}`} />
            )}

            <div className="relative z-10 h-full flex flex-col">
                {!user ? <LoginScreen onLogin={login} /> : (
                    activeGroup
                    ? <QuinielaApp user={user} group={activeGroup} onExit={() => setActiveGroup(null)} />
                    : <GroupsDashboard user={user} onSelectGroup={setActiveGroup} onCreateGroup={createGroup} onJoinGroup={joinGroup} onDeleteGroup={deleteGroup} onPurchase={buy} onShowPremiumModal={() => setModal(true)} />
                )}
            </div>
            {modal && <PremiumModal onClose={()=>setModal(false)}/>}
            {showUpdateNotice && user && <UpdateNotice onClose={handleCloseUpdateNotice} />}
        </div>
    );
}